/**
 * Translation dictionary for the consumer-facing /app surface.
 *
 * Keys are SHOUTY_SNAKE_CASE so they read as identifiers in the JSX, not as
 * accidental English copy. Placeholders use the `{name}` braces syntax which
 * the `t()` helper substitutes at call time.
 *
 * The two language dicts must stay key-aligned — the `satisfies` clause at
 * the bottom enforces that at the type level.
 */

export type Lang = "en" | "ro";

const en = {
  WELCOME_TITLE: "Welcome",
  WELCOME_SUBTITLE: "Pick your city to see what's open tonight.",
  WELCOME_CITY_PLACEHOLDER: "Select a city",
  WELCOME_CITY_SOON: "Soon",
  WELCOME_MORE_CITIES_SOON: "More cities soon. Search to check yours.",
  WELCOME_CITY_SEARCH: "Search city…",
  WELCOME_CITY_NONE: "No matching city",
  WELCOME_CITY_NEAREST: "Nearest",
  WELCOME_CITY_VENUE: "venue",
  WELCOME_CITY_VENUES: "venues",
  WELCOME_CONTINUE: "Continue",
  WELCOME_CITIES_ERROR: "We couldn't load the cities. Try again.",

  RETRY: "Try again",
  CLOSE: "Close",
  SCROLL_LEFT: "Scroll left",
  SCROLL_RIGHT: "Scroll right",
  TICKER_LABEL: "Live event ticker",
  MAP_CLUSTER: "{n} venues",
  DETAIL_GO_IMAGE: "Go to image {n}",

  HOME_TITLE: "Tonight near you",
  HOME_TITLE_DAY: "Today near you",
  HOME_SUBTITLE: "What's open, what's on offer and what's happening tonight, straight from each venue.",
  HOME_CHANGE_CITY: "Change city",
  // /showcase-cluj demo marker (docs/features/showcase.md).
  SHOWCASE_DEMO_PILL: "Demo · simulated data",
  SHOWCASE_SPEED_LABEL: "Simulation speed {speed}×, tap to change",
  HOME_CITY_EMPTY: "No venues in {city} yet. Check back soon.",
  HOME_LOAD_ERROR: "Couldn't load venues. Check your connection and try again.",
  HOME_TAB_RECOMMENDED: "Recommended",
  HOME_TAB_OFFERS: "Offers & Events",

  HOME_SEARCH_PLACEHOLDER: "Search venues, cuisines, areas…",
  HOME_SEARCH_CLEAR: "Clear search",
  HOME_FILTERS_RECOMMENDED: "Popular",
  HOME_FILTERS_CUSTOM: "Filters",
  HOME_FILTERS_TITLE: "Custom filters",
  HOME_FILTERS_CLEAR: "Clear all",
  HOME_FILTERS_CUISINES: "Cuisines",
  HOME_FILTERS_TAGS: "Tags",
  HOME_FILTERS_SHOW: "Show {n} places",
  HOME_FILTERS_NONE: "No venues match your filters.",
  HOME_RESULT_COUNT: "{n} places",
  HOME_RESULT_COUNT_ONE: "1 place",

  HOME_FILTER_OPEN_NOW: "Open now",
  HOME_FILTER_DISCOUNT: "Offer running",
  HOME_FILTER_QUICK: "Quick bites",
  HOME_FILTER_QUIET: "Not crowded",
  HOME_FILTER_SOME_SPACE: "Still has space",
  HOME_FILTER_NEARBY: "Within {km} km",

  AI_ASK_PILL: "Ask Resto",
  AI_ASK_HINT: "Turn this into filters",
  AI_ASK_PENDING: "Working on it…",
  AI_ASK_MISSED: "We couldn't turn that into filters, so we searched the text instead.",

  RECS_TITLE: "Recommended for you",
  RECS_SUBTEXT: "Based on your visits and ratings here",
  QUICK_TITLE: "Want something quick?",
  QUICK_SUBTEXT: "Fast, casual spots for when you're short on time.",
  NEARBY_TITLE: "Closest to you",
  NEARBY_ENABLE: "See what's closest",
  NEARBY_ENABLE_HINT: "Uses your location",
  NEARBY_LOADING: "Finding what's near you…",
  NEARBY_NONE: "No open restaurants near you right now.",
  NEARBY_DENIED: "Location is off. Enable it to see what's closest.",
  NEARBY_UNAVAILABLE: "Location is unavailable right now.",
  NEARBY_RETRY: "Enable location",
  NEARBY_DENIED_HINT: "If no prompt appears, allow location for this site in your browser settings.",
  NEARBY_TIMEOUT: "Couldn't get a location fix. Try again.",
  NEARBY_UNSUPPORTED: "This browser doesn't support location.",
  NEARBY_INSECURE: "Location needs a secure (https) connection.",

  MAP_OPEN: "Map",
  MAP_TITLE: "Live map",
  MAP_RECENTER: "Center on me",
  MAP_YOU: "You are here",

  OFFERS_EMPTY: "No offers in {city} right now.",
  OFFERS_SECTION: "Offers & events",
  OFFER_LIVE: "Live now",
  OFFER_EVERY_DAY: "Every day",
  OFFER_VIEW_VENUE: "View venue",

  LEVEL_GREEN: "Wide Open",
  LEVEL_YELLOW: "Filling Up",
  LEVEL_RED: "At Capacity",
  LEVEL_AVAILABLE: "Available",

  CARD_CLOSED: "Closed",
  CARD_OPEN: "Open",
  CARD_OPEN_UNTIL: "Open · until {time}",
  CARD_OPEN_UNTIL_SHORT: "until {time}",
  CARD_CLOSED_OPENS: "Closed · opens {time}",
  CARD_SEATS_AVAILABLE: "of {total} seats available",

  DETAIL_PREV_IMAGE: "Previous photo",
  DETAIL_NEXT_IMAGE: "Next photo",
  DETAIL_MAP_TITLE: "Open in Google Maps",
  DETAIL_MAP_SUBTITLE: "View location",

  RES_MARKER_NEUTRAL: "Takes reservations",
  RES_STATUS_AVAILABLE: "Reservations available",
  RES_STATUS_LIMITED: "Reservations limited",
  RES_STATUS_FULL: "Fully booked",
  RES_DETAIL_TITLE: "Reservations",
  RES_DETAIL_NEUTRAL: "This venue takes reservations.",
  RES_DETAIL_HINT: "Set by the venue as a guide for today. To book, contact the venue.",

  GOLIVE_CONFIRM_TITLE: "Ready to go live?",
  GOLIVE_CONFIRM_SUB: "Tap below to make this restaurant visible to everyone.",
  GOLIVE_CONFIRM_CTA: "Make it live",
  GOLIVE_ACTIVATING: "Activating…",
  GOLIVE_SUCCESS: "{name} is now live.",
  GOLIVE_SUCCESS_SUB: "The restaurant is now visible to everyone. Welcome to Resto.",
  GOLIVE_INVALID_TITLE: "This link is no longer valid",
  GOLIVE_INVALID_SUB: "It may have already been used. Ask for a fresh link to go live.",
  GOLIVE_OPEN_APP: "Open the app",

  LANG_TOGGLE_TO_EN: "Switch to English",
  LANG_TOGGLE_TO_RO: "Switch to Romanian",

  // ---- Marketing landing (/landing) ----
  LANDING_NAV_OPEN_APP: "Open the app",

  LANDING_TICKER_ARIA: "Example venue updates",
  LANDING_TICKER_EXAMPLE: "Example",
  LANDING_TICKER_OPEN_NOW: "open · tables available now",
  LANDING_TICKER_FILLING: "open · filling up",
  LANDING_TICKER_SEATS_LEFT: "open · {n} seats left",
  LANDING_TICKER_EVENT: "live set tonight · tables available",

  LANDING_HERO_TITLE_A: "Walk in",
  LANDING_HERO_TITLE_B: "now.",


  LANDING_RESTO_NOT_RES_TITLE: "Not a reservation system",
  LANDING_RESTO_NOT_RES_BODY: "Reservation platforms sell next Friday. We sell the next 30 to 60 minutes: people already out, already hungry, already nearby.",
  LANDING_RESTO_NEVER_EMPTY_TITLE: "Never shown as empty",
  LANDING_RESTO_NEVER_EMPTY_BODY: "The feed says one thing about you: open, with room now. A room that looks alive and still has a table draws people in. We never announce a quiet night.",

  LANDING_COMPARE_TITLE: "How Resto compares",
  LANDING_COMPARE_ROW_MOMENT: "Moment",
  LANDING_COMPARE_ROW_DATA: "Data",
  LANDING_COMPARE_ROW_EVENTS: "Events & offers",
  LANDING_COMPARE_ROW_FEE: "Per-cover fee",
  LANDING_COMPARE_ROW_WALKINS: "Brings walk-ins",
  LANDING_COMPARE_MOMENT_RESTO: "Next 30–60 min",
  LANDING_COMPARE_MOMENT_FUTURE: "Future booking",
  LANDING_COMPARE_MOMENT_PASSIVE: "Passive listing",
  LANDING_COMPARE_DATA_LIVE: "Live from POS",
  LANDING_COMPARE_DATA_MANUAL: "Manual",
  LANDING_COMPARE_DATA_CROWD: "Estimated",
  LANDING_COMPARE_EVENTS_RESTO: "Yes, live, right now",
  LANDING_COMPARE_EVENTS_BOOKING: "Yes, on bookings",
  LANDING_COMPARE_EVENTS_LIMITED: "Limited",
  LANDING_COMPARE_FEE_RESTO: "No, flat €50",
  LANDING_COMPARE_FEE_OT: "Yes ($1–1.50 / €2.60)",
  LANDING_COMPARE_FEE_FREE: "Free",
  LANDING_COMPARE_YES: "Yes",
  LANDING_COMPARE_NO: "No",


  LANDING_PEOPLE_PROBLEM: "You want to eat out now, but you can't tell who has a free table. So you call around, walk around, or give up and order in. Resto shows you live.",
  LANDING_PEOPLE_STEP1_TITLE: "Open the feed",
  LANDING_PEOPLE_STEP1_BODY: "Every venue in your city, the open ones first.",
  LANDING_PEOPLE_STEP2_TITLE: "Read the room",
  LANDING_PEOPLE_STEP2_BODY: "How full it is, in three colours. What's on tonight. Any offers.",
  LANDING_PEOPLE_STEP3_TITLE: "Tap and go",
  LANDING_PEOPLE_STEP3_BODY: "One tap opens directions. You're at the table in twenty minutes.",
  LANDING_PEOPLE_NO_LOGIN: "Browse without an account. Live capacity needs a free one. In English or Romanian.",

  LANDING_DEMO_PCT_FULL: "{n}% full",
  LANDING_DEMO_PILL_GREEN: "open · tables available now",
  LANDING_DEMO_PILL_YELLOW: "open · filling up",
  LANDING_DEMO_PILL_RED: "at capacity right now",
  LANDING_DEMO_TAG_VATRA: "Wood-fired · Mănăștur",
  LANDING_DEMO_TAG_SAMSARA: "Plant-forward · Centru",
  LANDING_DEMO_TAG_KAISEKI: "Omakase · Andrei Mureșanu",
  LANDING_DEMO_TAG_MOSAIC: "Levantine · Piața Muzeului",

  LANDING_FAQ_TITLE: "Honest answers.",
  LANDING_FAQ_Q1: "How is this different from OpenTable or another reservation platform?",
  LANDING_FAQ_Q2: "Where does the capacity data come from?",
  LANDING_FAQ_A2: "From the venue's POS. We read which tables are occupied, about every 90 seconds, and the level on the card follows.",
  LANDING_FAQ_Q3: "What does Resto charge?",
  LANDING_FAQ_Q4: "What else can be seen about a restaurant?",
  LANDING_FAQ_A4: "Opening hours, offers and upcoming events for each restaurant. More will follow in time.",
  LANDING_FOOTER_COPYRIGHT: "© Resto {year}",

  // --- Account sidebar -------------------------------------------------------
  ACCOUNT_BUTTON: "Account & settings",
  SIDEBAR_TITLE: "Account & settings",
  SIDEBAR_GUEST_TITLE: "Browsing as a guest",
  SIDEBAR_GUEST_SUB: "The feed works without an account. A free account adds live capacity, every offer and recommendations based on your visits.",
  SIDEBAR_SIGNIN_CTA: "Sign in / Create account",
  SIDEBAR_SECTION_ACCOUNT: "Account",
  SIDEBAR_SECTION_SETTINGS: "Settings",
  SIDEBAR_DISPLAY_NAME: "Display name",
  SIDEBAR_SAVE: "Save",
  SIDEBAR_SAVED: "Saved.",
  SIDEBAR_EMAIL: "Email",
  SIDEBAR_EMAIL_VERIFIED: "Verified",
  SIDEBAR_EMAIL_UNVERIFIED: "Not verified",
  SIDEBAR_PROVIDER_GOOGLE: "Signed in with Google",
  SIDEBAR_PROVIDER_EMAIL: "Email & password account",
  SIDEBAR_CHANGE_PASSWORD: "Change password",
  SIDEBAR_PASSWORD_UPDATED: "Password changed.",
  SIDEBAR_SIGNOUT: "Sign out",
  SIDEBAR_DELETE_ACCOUNT: "Delete account",
  SIDEBAR_DELETE_CONFIRM_TITLE: "Delete your account?",
  SIDEBAR_DELETE_CONFIRM_BODY:
    "This permanently removes your account and the data linked to it. The app keeps working without one.",
  SIDEBAR_DELETE_CONFIRM_CTA: "Delete permanently",
  SIDEBAR_CANCEL: "Cancel",
  SIDEBAR_LANGUAGE: "Language",
  SIDEBAR_THEME: "Theme",
  THEME_DARK: "Dark",
  THEME_LIGHT: "Light",
  THEME_TOGGLE_TO_DARK: "Switch to dark theme",
  THEME_TOGGLE_TO_LIGHT: "Switch to light theme",
  SIDEBAR_LOCATION: "Location",
  SIDEBAR_LOCATION_ON: "Enabled",
  SIDEBAR_LOCATION_OFF: "Off",
  SIDEBAR_LOCATION_PROMPTING: "Waiting for permission…",
  SIDEBAR_LOCATION_DENIED: "Blocked in browser settings",
  SIDEBAR_LOCATION_UNAVAILABLE: "Unavailable on this device",
  SIDEBAR_LOCATION_ENABLE: "Enable",
  SIDEBAR_CLOSE: "Close menu",
  SIDEBAR_STAFF_MANAGE: "Manage your venue",
  SIDEBAR_STAFF_ADMIN: "Operator dashboard",
  SIDEBAR_VERSION: "Version {v}",
  SIDEBAR_TERMS: "Terms",
  SIDEBAR_PRIVACY: "Privacy",

  // --- Consumer auth (sidebar views) ----------------------------------------
  AUTH_SIGNIN_TITLE: "Sign in",
  AUTH_SIGNUP_TITLE: "Create account",
  AUTH_EMAIL_LABEL: "Email",
  AUTH_PASSWORD_LABEL: "Password",
  AUTH_NEW_PASSWORD_LABEL: "New password",
  AUTH_NAME_LABEL: "Name",
  AUTH_NAME_HINT: "Shown in your account menu. Any name works.",
  AUTH_PW_RULE: "At least 8 characters, with a letter and a digit",
  AUTH_SIGNIN_CTA: "Sign in",
  AUTH_SIGNUP_CTA: "Create account",
  AUTH_GOOGLE_CTA: "Continue with Google",
  AUTH_APPLE_CTA: "Continue with Apple",
  AUTH_OR: "or",
  AUTH_TO_SIGNUP: "New here? Create an account",
  AUTH_TO_SIGNIN: "Already have an account? Sign in",
  AUTH_FORGOT: "Forgot password?",
  AUTH_FORGOT_TITLE: "Reset password",
  AUTH_FORGOT_CTA: "Send reset link",
  AUTH_FORGOT_SENT:
    "If an account exists for that email, a reset link is on its way. Open it on this device.",
  AUTH_RESET_TITLE: "Set a new password",
  AUTH_RESET_CTA: "Save new password",
  AUTH_RESET_DONE: "Password updated.",
  AUTH_CONFIRM_TITLE: "Confirm your email",
  AUTH_CONFIRM_BODY: "We sent a confirmation link to {email}. Open it, then sign in.",
  AUTH_CONFIRM_RESEND: "Resend email",
  AUTH_CONFIRM_RESENT: "Sent.",
  AUTH_ERR_INVALID: "Wrong email or password.",
  AUTH_ERR_EMAIL_TAKEN: "That email can't be used. Try signing in instead.",
  AUTH_ERR_WEAK_PASSWORD: "Password must be at least 8 characters.",
  AUTH_ERR_UNCONFIRMED: "Confirm your email first. Check your inbox.",
  AUTH_ERR_RATE_LIMIT: "Too many attempts. Wait a minute and try again.",
  AUTH_ERR_CAPTCHA: "Verification failed. Please try again.",
  AUTH_COOLDOWN_WAIT: "Too many attempts. Wait {seconds}s.",
  AUTH_ERR_GENERIC: "Something went wrong. Try again.",

  // --- Anonymous-ID consent notice -------------------------------------------
  CONSENT_ARIA: "Privacy notice",
  CONSENT_BODY: "Can we remember this device with an anonymous ID? It helps us count visits without an account. No ads.",
  CONSENT_ACCEPT: "Allow",
  CONSENT_DECLINE: "No thanks",
  CONSENT_MORE: "Details",

  // --- Conversion: Dine In check-in + feedback --------------------------------
  DINE_IN_CTA: "Dine In!",
  DINE_IN_INFO_LABEL: "What does check-in do?",
  DINE_IN_EXPLAINER:
    "Checks that you're within 75 m, then records only this venue and today's date. Your position isn't stored. At a Resto Bonus venue, a signed-in tap also collects a stamp. Automatic visits stay off unless you turn them on in Settings.",
  DINE_IN_ENABLE_LOCATION: "Enable location to check in",
  DINE_IN_DONE: "You're checked in",
  DINE_IN_LEAVE_FEEDBACK: "Leave feedback",
  DINE_IN_ERROR: "Couldn't check in. Try again.",
  DINE_IN_STAMP_EARNED: "Stamp {n} of {total}",
  DINE_IN_STAMP_MINTED: "Card full: you have a bonus!",
  DINE_IN_STAMP_LOW_ACCURACY:
    "No stamp yet: your location is ±{m} m and a stamp needs {max} m or better. Try again in a moment.",
  DINE_IN_STAMP_TOO_NEW: "Stamps start {hours} hours after you create your account.",
  DINE_IN_STAMP_DAILY: "You already have today's stamp. Come back tomorrow.",
  DINE_IN_STAMP_ALREADY: "This visit already has its stamp.",
  DINE_IN_STAMP_CAPPED: "Your card is full until you use your bonus.",
  DINE_IN_STAMP_FROZEN: "Stamps are paused on this device. Write to us if this is a mistake.",
  DINE_IN_STAMP_TOO_FAST: "No stamp: it's too soon after a stamp at a venue far from here.",
  DINE_IN_OPEN_CARD: "Stamp card",
  GEO_VISIT_TOAST: "We counted your visit to {venue}.",
  FEEDBACKS_TITLE: "Your visits",
  FEEDBACKS_ENTRY: "Visits & feedback",
  FEEDBACKS_EMPTY: "No visits yet. Check in at a restaurant to leave feedback.",
  FEEDBACK_STAR: "{n} stars",
  FEEDBACK_RATING_LABEL: "Your rating",
  FEEDBACK_STARS_HINT: "Tap a star to rate",
  FEEDBACK_SPEND_LABEL: "About how much per person (RON)",
  FEEDBACK_SPEND_PLACEHOLDER: "e.g. 120",
  FEEDBACK_MESSAGE_LABEL: "Your feedback (optional)",
  FEEDBACK_MESSAGE_PLACEHOLDER: "How was it?",
  FEEDBACK_SUBMIT: "Submit",
  FEEDBACK_SKIP: "Skip",
  FEEDBACK_ERROR: "Couldn't save. Try again.",
  FEEDBACK_LEAVE: "Leave feedback",
  FEEDBACK_EDIT: "Edit",
  FEEDBACK_SPEND_SUMMARY: "{amount} RON/person",
  RATING_PROMPT_TITLE: "Rate your visit",
  RATING_PROMPT_BODY: "How was {venue}?",

  // --- Account-gated surfaces (guest → sign-up conversion) ---------------------
  OFFERS_LOCKED_TITLE: "Offers, all in one place",
  OFFERS_LOCKED_BODY: "Create a free account to browse every offer and event in {city}.",
  OFFERS_LOCKED_TAB_HINT: "Free account required",
  SIGNUP_FREE_CTA: "Create free account",
  SIGNUP_NUDGE_TITLE: "Get more with a free account",
  SIGNUP_NUDGE_BODY: "See live availability at connected venues, open the Offers tab and keep your visits and feedback. Free.",

  // --- Legal pages (It-6: Art. 13 privacy policy + terms; RO is binding) ---------
  LEGAL_BACK: "Back to the app",
  LEGAL_UPDATED: "Last updated: October 2026",
  LEGAL_BINDING: "The Romanian text is the binding version.",
  LEGAL_AND: "and",
  LEGAL_IDENTITY: "Run by {controllers}",
  LEGAL_TERMS_TITLE: "Terms of use",
  LEGAL_TERMS_INTRO:
    "These terms apply when you use Resto (restonow.app). By creating an account you accept them.",
  LEGAL_T1_H: "The service",
  LEGAL_T1_P:
    "Resto shows restaurants in Romanian cities, with live availability where a venue shares it, plus offers and events. Browsing venues is free; live availability and the Offers tab need a free account. Resto Bonus, a stamp card at participating venues, follows its own rules (restonow.app/bonus-rules), which are part of these terms. Resto is in a pilot phase, so features may change.",
  LEGAL_T2_H: "Your account",
  LEGAL_T2_P:
    "You must be 16 or older to create an account. Keep your password safe; you are responsible for what happens on your account. You can delete it any time from the account menu.",
  LEGAL_T3_H: "Venue information",
  LEGAL_T3_P:
    "Availability, events, offers and opening hours come from the venues or their POS systems. We show them in good faith but can't guarantee them, and a free table can be taken between the app and the door. Offers are honoured by the venue, on the venue's conditions.",
  LEGAL_T4_H: "Your feedback",
  LEGAL_T4_P:
    "Feedback you leave (stars, spend, a note) stays private. You allow us to use it to run Resto: to show venues aggregate numbers without your identity, to produce AI summaries of recurring themes for our team, and to improve the service. Don't post anything unlawful or abusive, or other people's personal data.",
  LEGAL_T5_H: "Fair use",
  LEGAL_T5_P:
    "No scraping at scale (our public API and MCP endpoint are open within their limits), no probing accounts that aren't yours, no attempts to disrupt the platform, no fake check-ins or feedback. A username must not impersonate someone else or be offensive; we may change or reclaim one that is. We may suspend accounts that break these terms.",
  LEGAL_T6_H: "Reports and moderation",
  LEGAL_T6_P1:
    "Our single point of contact for users and authorities (EU Digital Services Act, Arts. 11–12) is {email}, in Romanian or English.",
  LEGAL_T6_P2:
    "To report content that is illegal or wrong (a venue, its offers or events), tap Report on the venue or the offer and send the short form from your account, or write to us. Tell us what and where, why it's a problem, and confirm you report in good faith; with an account we already know your email. A person reviews every report. If we remove or restrict content, we tell the affected party why and how to contest the decision.",
  LEGAL_T7_H: "Liability",
  LEGAL_T7_P:
    "Resto is provided as it is during the pilot. As far as the law allows, we are not liable for decisions you make based on the information shown, or for what happens at a venue. Nothing here limits the rights you have as a consumer.",
  LEGAL_T8_H: "Law and language",
  LEGAL_T8_P:
    "Romanian law applies. Disputes go to the competent Romanian courts, without affecting your rights as a consumer. If the English and Romanian texts differ, the Romanian text prevails.",
  LEGAL_T9_H: "Contact",
  LEGAL_T9_P: "Resto is run by {controllers}. Questions: {email}.",
  LEGAL_PRIVACY_TITLE: "Privacy policy",
  LEGAL_PRIVACY_INTRO:
    "This policy explains what personal data Resto (restonow.app) processes, why, on what legal basis, for how long, who helps us, and what your rights are.",
  LEGAL_P1_H: "Who we are",
  LEGAL_P1_P:
    "Resto is run by {controllers}, natural persons who decide together how your data is used (joint controllers) while the company is being set up. For any privacy question or request, write to {email}.",
  LEGAL_P2_H: "What we process and why",
  LEGAL_P2_L1: "Usage events. When you use Resto we record events (page and venue views, directions and share taps) with the city and the screen. They carry no identifier unless you allowed the anonymous device ID or are signed in. Why: to understand how Resto is used and improve it. Legal basis: our legitimate interest (GDPR Art. 6(1)(f)). Kept 180 days.",
  LEGAL_P2_L2:
    "Anonymous device ID. Only if you tap Allow: a random ID stored on your device that connects your events and visits without an account, and links them to your account if you create one. Legal basis: your consent (GDPR Art. 6(1)(a); Law 506/2004). Withdraw it any time: Settings → Forget this device.",
  LEGAL_P2_L3:
    "Your account. Email, display name, password (stored only as a hash by our authentication provider) or your Google sign-in, language and theme, and your confirmation that you are 16 or older. Legal basis: the contract with you, i.e. our Terms (GDPR Art. 6(1)(b)). Kept until you delete the account.",
  LEGAL_P2_L4:
    "Visits and feedback. When you tap Dine In, or have automatic visits on, we record which venue and which day. Feedback you add (stars, approximate spend, an optional note) is attached to that visit. Legal basis: the contract, for features you choose to use (GDPR Art. 6(1)(b)). Visits are kept 24 months; after that only monthly per-venue totals remain, with no link to you.",
  LEGAL_P2_L5:
    "Security. A captcha on sign-in and sign-up forms, rate limits keyed by IP address (held in memory for minutes) and server logs. Legal basis: our legitimate interest in keeping Resto safe (GDPR Art. 6(1)(f)).",
  LEGAL_P2_L6:
    "Friends. If you use Friends: your username, the requests you send and receive, your friends, the people you block and your invite link. Your friends, and anyone who opens your invite link, see only your display name and username; nothing else about you is shared with them. Legal basis: the contract, for a feature you choose to use (GDPR Art. 6(1)(b)). Kept until you remove the friend or delete the account; unanswered requests are deleted after 90 days and invite links stop working after 7 days.",
  LEGAL_P2_L7:
    "Resto Bonus. If you collect stamps: each stamp (the venue and the day), your bonus, when and where you used it, and wrong PIN attempts (to stop guessing). When you tap Dine In! or use a bonus, your position and its accuracy are checked once on our server and are not stored. Venues see only totals and a log of when a bonus was used and for which item, never who. Legal basis: the contract, under the Bonus rules (GDPR Art. 6(1)(b)). How long we keep it: see below.",
  LEGAL_P2_L8:
    "Reports. If you send a report: what you wrote, what it is about (the app or a venue, and the page), an optional screenshot, your account and the time. Only Resto's team sees it. Legal basis: our legal obligation to handle notices (EU Digital Services Act, Art. 16; GDPR Art. 6(1)(c)) and our legitimate interest in fixing problems (Art. 6(1)(f)). Kept until we handle it, then 180 days.",
  LEGAL_P2_L9:
    "Partner requests. If you send the Become a partner form for a restaurant: your name and role, email, phone, the restaurant, its city, POS and size, and your message. We check that the email's domain can receive mail and keep a one-way hash of your IP address to stop floods. Legal basis: steps you asked for before a contract (GDPR Art. 6(1)(b)) and our legitimate interest in answering you (Art. 6(1)(f)). Kept while we talk; requests that lead nowhere, and spam, are deleted after 12 months.",
  LEGAL_P3_H: "Location",
  LEGAL_P3_P:
    "Resto uses your location only while it is open on screen, and only after you allow it in your browser. It sorts venues by distance and shows the Dine In button within 75 m of a venue. When you check in or use a bonus, your position is sent once so our server can confirm that distance; it is not stored, and we keep only the venue and the day. Automatic visits (a visit counted after 10 minutes at a venue you opened) stay off until you turn them on in Settings. Resto never tracks your location in the background.",
  LEGAL_P4_H: "On your device",
  LEGAL_P4_P:
    "Resto keeps on your device: your language and theme, your consent choice, the anonymous device ID if you allowed it, a short list of venues you opened (to order your feed), whether automatic visits are on, when we last asked for a rating, a friend invite you opened before signing in (for up to an hour), whether we already suggested an account during this visit and, when you are signed in, your session. No advertising or third-party tracking cookies.",
  LEGAL_P5_H: "Feedback and AI",
  LEGAL_P5_P1:
    "Your feedback is private. Venues see only counts and averages (visits, average rating, average spend), never your name or email. Resto's own team sees the same numbers plus short AI summaries of recurring themes, and accesses the records only to run and protect the service. The summaries are written by an AI model from a venue's recent notes, so the note text is sent to our AI provider for that purpose only. When you use Ask Resto, what you type is sent to the AI provider to turn it into filters. Some venue descriptions and offer texts are drafted with AI and checked by staff before they are published.",
  LEGAL_P5_P2:
    "Until an AI provider is switched on, these features run on built-in rules and nothing is sent anywhere; once one is, we will name it here.",
  LEGAL_P6_H: "Who helps us",
  LEGAL_P6_L1: "Supabase: database, accounts and account emails, hosted in Frankfurt (EU).",
  LEGAL_P6_L2: "Fly.io: our API server, in its Frankfurt (EU) region.",
  LEGAL_P6_L3: "Netlify: hosts the website and delivers it worldwide.",
  LEGAL_P6_L4: "Google: only if you sign in with Google.",
  LEGAL_P6_L5: "Cloudflare: the Turnstile captcha on sign-in and sign-up forms and the partner form.",
  LEGAL_P6_L6: "CARTO: map tiles your browser loads when you open the map (CARTO sees your IP address).",
  LEGAL_P6_L7: "Our AI provider: only for the AI features above, once switched on.",
  LEGAL_P6_P:
    "Several of these are US companies or may process data outside the EU. Such transfers rely on the EU–US Data Privacy Framework or the European Commission's standard contractual clauses. We don't sell your data or use it for advertising.",
  LEGAL_P7_H: "How long we keep data",
  LEGAL_P7_L1: "Usage events: 180 days.",
  LEGAL_P7_L2:
    "Visits and feedback: 24 months, then only monthly per-venue totals with no link to you.",
  LEGAL_P7_L3: "Account data: until you delete your account.",
  LEGAL_P7_L4:
    "Guest data under an anonymous device ID: until you forget the device, within the limits above.",
  LEGAL_P7_L5:
    "Friends: until you remove the friend or delete the account; unanswered requests 90 days; invite links 7 days.",
  LEGAL_P7_L6:
    "Resto Bonus: stamps 13 months (each is valid for 12); bonuses and the record of used bonuses 24 months; an unconfirmed bonus screen 1 day; wrong PIN attempts 30 days.",
  LEGAL_P7_L7: "Reports: 180 days after we handle them.",
  LEGAL_P7_L8: "Partner requests: while we talk; those that lead nowhere, and spam, 12 months.",
  LEGAL_P7_P: "These limits are applied automatically every night.",
  LEGAL_P8_H: "Your rights",
  LEGAL_P8_P1:
    "You have the right to access your data, have it corrected or deleted, restrict its processing, receive it in a portable format, and object to processing based on our legitimate interest. Where we rely on your consent, you can withdraw it any time; that doesn't affect what happened before.",
  LEGAL_P8_P2:
    "In the app: delete your account from the account menu (this also erases your visits, feedback, events, friends and bonus data; a used bonus stays in the venue's log with no link to you), or forget this device in Settings. For anything else write to {email}; we answer within one month. You can also complain to the Romanian data protection authority, ANSPDCP (dataprotection.ro).",
  LEGAL_P9_H: "Age",
  LEGAL_P9_P:
    "Resto accounts are for people aged 16 or older. If you think someone younger made an account, write to us and we'll delete it.",
  LEGAL_P10_H: "Changes",
  LEGAL_P10_P: "When what we do with data changes, we update this policy and the date at the top.",
  LEGAL_BONUS_TITLE: "Resto Bonus rules",
  LEGAL_BONUS_INTRO:
    "Version 1.0, October 2026. These rules govern Resto Bonus, a stamp card organised by {controllers}. It runs only at venues that choose to take part; the app lists them.",
  LEGAL_B1_H: "Who can take part",
  LEGAL_B1_P:
    "Anyone aged 16 or over with a Resto account. Staff accounts (venue managers and the Resto team) can't take part. Stamps start counting {minAccountAgeHours} hours after the account is created. Collecting stamps costs nothing.",
  LEGAL_B2_H: "Collecting stamps",
  LEGAL_B2_L1:
    "You earn a stamp when you tap \"Dine In!\" at a participating venue while you are there: your phone's location must be within 75 m of the venue and accurate to 50 m or better. The location is used for this check and is not stored.",
  LEGAL_B2_L2: "At most 1 stamp counts per day (Romanian time), across all venues.",
  LEGAL_B2_L3: "Visits counted automatically (the \"Count my visits automatically\" setting) never earn stamps.",
  LEGAL_B2_L4: "A stamp is valid for 12 months.",
  LEGAL_B3_H: "The bonus",
  LEGAL_B3_L1:
    "{stampsRequired} stamps earn one bonus: one item set by the venue where you use it, valid with any order. The item is non-alcoholic and costs at most {maxItemRon} RON on the menu. A venue may ask for a minimum order; it is shown next to the item.",
  LEGAL_B3_L2: "You hold one bonus at a time. While you hold one, you can collect up to {stampsWhileHolding} stamps towards the next.",
  LEGAL_B3_L3:
    "A bonus is valid for 90 days from the day you earn it. It can't be exchanged for cash, given to someone else or combined with another bonus.",
  LEGAL_B4_H: "Using the bonus",
  LEGAL_B4_L1:
    "Use it at any participating venue. At the venue (within 75 m, with the same location check as for stamps), tap \"Use bonus\" and show the screen to the staff: it shows the venue, the item and a live 3-minute countdown. A venue confirms at most {maxPerVenueDay} bonuses a day; after that, use yours there the next day or at another participating venue.",
  LEGAL_B4_L2:
    "A staff member types the venue's PIN on your phone to confirm. After {pinMaxTries} wrong PINs, using the bonus at that venue is paused for {pinLockMinutes} minutes; after {pinMaxFailsPerDay} wrong PINs in one day, at any venues, it is paused until the next day.",
  LEGAL_B4_L3: "Once confirmed, the bonus is used and can't be used again.",
  LEGAL_B5_H: "When a venue changes its item or leaves",
  LEGAL_B5_P:
    "A venue can change its bonus item with 7 days' notice, shown in the app; you get the item shown when you start using your bonus. A venue that leaves the program still honours, for 30 days, the bonuses earned before it left.",
  LEGAL_B6_H: "Fair play",
  LEGAL_B6_P:
    "Stamps and bonuses are personal. We may cancel stamps or bonuses obtained by faking a location, by using several accounts on one device or by any other abuse, and pause an account's stamps while we look into it. A venue may refuse a bonus without an order.",
  LEGAL_B7_H: "No element of chance",
  LEGAL_B7_P: "Your bonus depends only on the stamps you collect. There is no draw, lottery or other element of chance.",
  LEGAL_B8_H: "Your data",
  LEGAL_B8_P:
    "Your stamps, bonus and redemptions are kept with your account. Venues see only totals and a log of when a bonus was used and for which item, never who used it.",
  LEGAL_B9_H: "Changes to these rules",
  LEGAL_B9_P:
    "We publish each new version on this page, with its number and date, before it applies. A change never cancels stamps or a bonus you already hold.",
  LEGAL_B10_H: "Contact",
  LEGAL_B10_P: "Questions or complaints about Resto Bonus: {email}.",

  // --- It-6 privacy controls, 16+, Report link, AI label -------------------------
  SIDEBAR_AUTO_VISITS: "Count my visits automatically",
  SIDEBAR_AUTO_VISITS_HINT:
    "Counts a visit after 10 minutes at a venue you opened, while Resto is on screen. Stores only the venue and the day.",
  SIDEBAR_DEVICE_ID: "Anonymous device ID",
  SIDEBAR_DEVICE_ID_ON: "On: counts your visits without an account.",
  SIDEBAR_DEVICE_ID_OFF: "Off.",
  SIDEBAR_DEVICE_ALLOW: "Allow",
  SIDEBAR_DEVICE_FORGET: "Forget this device",
  SIDEBAR_DEVICE_FORGET_CONFIRM: "Tap again to erase",
  SIDEBAR_DEVICE_FORGOTTEN: "Done. Guest data from this device is erased.",
  SIDEBAR_DEVICE_FORGET_ERROR: "Couldn't erase it right now, so nothing was changed. Try again later.",
  AUTH_AGE_16: "I'm 16 or older and I accept the",
  AUTH_AGE_TERMS: "Terms",
  AUTH_GOOGLE_AGE_NOTE:
    "New to Resto? Creating an account with Google confirms you're 16 or older and accept the Terms.",
  AUTH_SOCIAL_AGE_NOTE:
    "New to Resto? Creating an account with Google or Apple confirms you're 16 or older and accept the Terms.",
  REPORT_LINK: "Report",
  REPORT_ARIA: "Report {what}",
  REPORT_SUBJECT: "Report: {what}",
  REPORT_BODY:
    "What and where: {what} ({url})\nWhy it's a problem:\n\nYour name:\nYour email:\n\nI confirm I'm reporting in good faith and that the information above is accurate.",
  AI_ASK_SUGGESTED: "Filters suggested by AI",

  // --- Venue pages, 404, page titles (Oct 28; venue-detail-offers.md, seo-geo.md) ---
  DETAIL_HOURS_DAILY: "Daily {hours}",
  DETAIL_SHARE: "Share",
  DETAIL_LINK_COPIED: "Link copied",
  NOT_FOUND_TITLE: "This page isn't on the menu",
  NOT_FOUND_BODY: "The link may be old, or the place isn't listed anymore. Pick up from here.",
  NOT_FOUND_CITY: "Restaurants in Cluj-Napoca",
  NOT_FOUND_HOME: "Choose a city",
  META_NOT_FOUND_TITLE: "Page not found | Resto",
  META_WELCOME_TITLE: "Resto: see where there's room tonight",
  META_WELCOME_DESC: "Resto shows which restaurants, cafés and bars are open right now, their offers while they run and, with a free account, how full they are, read live from the venue's POS.",
  META_CITY_TITLE: "Restaurants in {city}: open now and live capacity | Resto",
  META_CITY_DESC: "See which restaurants, cafés and bars in {city} are open right now, their offers and, with a free account, how full they are.",
  META_VENUE_TITLE: "{name}, {city} | Resto",
  META_VENUE_DESC: "{name} in {city}: opening hours, photos, offers and live availability on Resto.",
  META_LEGAL_SUFFIX: "{title} | Resto",
  META_LANDING_TITLE: "Resto: live restaurant availability, for diners and venues",
  META_LANDING_DESC: "How Resto works: open now, offers and live capacity read from the venue's POS for diners, and one flat monthly price for restaurants.",
  META_MANAGE_TITLE: "Venue panel | Resto",
  // --- Reports (§35b; docs/features/reports.md) ------------------------------------
  SIDEBAR_REPORT_ENTRY: "Report a problem",
  REPORT_TITLE: "Report a problem",
  REPORT_KIND_APP: "The app",
  REPORT_KIND_VENUE: "A restaurant",
  REPORT_ABOUT: "About: {what}",
  REPORT_VENUE_HOWTO: "To report a restaurant, open its page and tap Report at the bottom. It comes here with the restaurant filled in.",
  REPORT_BODY_LABEL: "What's wrong?",
  REPORT_BODY_HINT_APP: "What you did, what you expected, what happened.",
  REPORT_BODY_HINT_VENUE: "Wrong info, a place that's closed for good, an offer that isn't real…",
  REPORT_BODY_TOO_SHORT: "A few more words, please (at least 10 characters).",
  REPORT_SCREENSHOT: "Screenshot (optional)",
  REPORT_SCREENSHOT_ADD: "Add a screenshot",
  REPORT_SCREENSHOT_REMOVE: "Remove",
  REPORT_SCREENSHOT_ONLY: "One image (PNG, JPG or WebP). No videos.",
  REPORT_SCREENSHOT_BAD: "That isn't an image we can use. Pick a screenshot (PNG, JPG or WebP).",
  REPORT_GOOD_FAITH: "I'm reporting in good faith and what I wrote is accurate.",
  REPORT_SEND: "Send report",
  REPORT_SENDING: "Sending…",
  REPORT_SENT_TITLE: "Thanks, we got it",
  REPORT_SENT_BODY: "Someone on our team reads every report. If we need more, we'll write to your account email.",
  REPORT_DONE: "Done",
  REPORT_ERR_RATE: "You can send one report a day. Your next one: {time}.",
  REPORT_ERR_UNCONFIRMED: "Confirm your email first, then you can send a report.",
  REPORT_ERR_GENERIC: "Couldn't send it. Try again in a moment.",
  REPORT_ERR_UPLOAD: "The screenshot didn't upload. Try again, or send without it.",
  REPORT_GUEST_TITLE: "Sign in to report",
  REPORT_GUEST_BODY: "Reports come from accounts, so we can follow up and keep spam out. No account? Write to us by email instead.",
  REPORT_GUEST_SIGNIN: "Sign in",

  // --- Friends (It-8, §32; docs/features/friends.md) ----------------------------
  FRIENDS_TITLE: "Friends",
  FRIENDS_ENTRY: "Your friends",
  FRIENDS_ENTRY_PICK: "Pick a username to start",
  FRIENDS_REQUESTS_BADGE: "{n} friend requests waiting",
  FRIENDS_LOADING: "Loading friends",
  FRIENDS_ERROR: "Something went wrong. Try again.",
  FRIENDS_RETRY: "Try again",
  FRIENDS_PRIVACY: "Friends see only your name and @username. Nothing else is shared.",
  FRIENDS_USERNAME_INTRO: "Pick a username so friends can add you. You can change it later.",
  FRIENDS_USERNAME_LABEL: "Username",
  FRIENDS_USERNAME_HINT: "3 to 20 characters: a-z, 0-9 and _",
  FRIENDS_USERNAME_SAVE: "Save username",
  FRIENDS_USERNAME_TAKEN: "That username is taken. Try another one.",
  FRIENDS_USERNAME_RESERVED: "That username is reserved. Try another one.",
  FRIENDS_USERNAME_INVALID: "Use 3 to 20 characters: a-z, 0-9 and _.",
  FRIENDS_USERNAME_CHANGE: "Change",
  FRIENDS_CANCEL: "Cancel",
  FRIENDS_INVITE_TITLE: "Invite a friend",
  FRIENDS_INVITE_BODY: "Anyone who opens your link can add you straight away. It works for 7 days.",
  FRIENDS_INVITE_COPY: "Copy link",
  FRIENDS_INVITE_COPIED: "Copied",
  FRIENDS_INVITE_SHARE: "Share",
  FRIENDS_INVITE_SHARE_TEXT: "Add me as a friend on Resto",
  FRIENDS_INVITE_QR: "QR code",
  FRIENDS_INVITE_VALID_UNTIL: "Link valid until {d}",
  FRIENDS_INVITE_RESET: "New link",
  FRIENDS_INVITE_RESET_CONFIRM: "The old link will stop working. Tap again to confirm",
  FRIENDS_QR_TITLE: "Scan to add @{u}",
  FRIENDS_QR_CLOSE: "Close",
  FRIENDS_ADD_TITLE: "Add by username",
  FRIENDS_ADD_PLACEHOLDER: "username",
  FRIENDS_ADD_SEND: "Send request",
  FRIENDS_ADD_SENT: "Request sent.",
  FRIENDS_ADD_FRIENDS: "They had already asked you. You're now friends.",
  FRIENDS_ADD_ALREADY_FRIENDS: "You're already friends.",
  FRIENDS_ADD_ALREADY_SENT: "You already sent them a request.",
  FRIENDS_ADD_NOT_FOUND: "No one has that username. Check the spelling.",
  FRIENDS_ADD_SELF: "That's your own username.",
  FRIENDS_ADD_BLOCKED: "You blocked this person. Unblock them first.",
  FRIENDS_RATE_LIMITED: "That's the limit for today. Try again tomorrow.",
  FRIENDS_REQUESTS_TITLE: "Requests",
  FRIENDS_ACCEPT: "Accept",
  FRIENDS_DECLINE: "Decline",
  FRIENDS_LIST_TITLE: "Friends",
  FRIENDS_EMPTY: "No friends yet. Share your link or add someone by username.",
  FRIENDS_SENT_TITLE: "Sent",
  FRIENDS_MORE: "More options for {name}",
  FRIENDS_REMOVE: "Remove",
  FRIENDS_REMOVE_CONFIRM: "Tap again to remove",
  FRIENDS_BLOCK: "Block",
  FRIENDS_BLOCK_CONFIRM: "Tap again to block",
  FRIENDS_BLOCKED_TITLE: "Blocked ({n})",
  FRIENDS_UNBLOCK: "Unblock",
  FRIEND_INVITE_TITLE: "{name} invited you to be friends on Resto",
  FRIEND_INVITE_SUB: "Friends see only each other's name and @username.",
  FRIEND_INVITE_SIGNED_OUT_SUB: "You need a free account to add friends. You'll come back here after.",
  FRIEND_INVITE_SIGNUP: "Create a free account",
  FRIEND_INVITE_SIGNIN: "I already have an account",
  FRIEND_INVITE_ADD: "Add @{u}",
  FRIEND_INVITE_PICK_USERNAME: "First, pick your own username.",
  FRIEND_INVITE_SUCCESS: "You and {name} are now friends",
  FRIEND_INVITE_ALREADY: "You and {name} are already friends",
  FRIEND_INVITE_SELF: "This is your own invite link",
  FRIEND_INVITE_SELF_SUB: "Send it to a friend so they can add you.",
  FRIEND_INVITE_INVALID_TITLE: "This invite doesn't work",
  FRIEND_INVITE_INVALID_SUB: "It may have expired or been replaced. Ask your friend for a new link.",
  FRIEND_INVITE_STAFF: "Staff accounts can't add friends. Use a personal account.",
  FRIEND_INVITE_OPEN_FRIENDS: "Open Friends",
  FRIEND_INVITE_OPEN_APP: "Open the app",

  // --- Bonus (It-10, §33; docs/features/bonus.md) -------------------------------
  BONUS_TITLE: "Bonus",
  BONUS_ENTRY: "Stamp card",
  BONUS_ENTRY_PROGRESS: "{n} of {total} stamps",
  BONUS_ENTRY_READY: "Bonus ready to use",
  BONUS_CARD_ARIA: "{n} of {total} stamps collected",
  BONUS_HOW:
    "Tap Dine In! at a participating venue to collect a stamp, at most {perDay} a day. {total} stamps earn one bonus.",
  BONUS_STAMPED_TODAY: "You collected a stamp today.",
  BONUS_STAMP_TTL: "Each stamp is valid for {days} days.",
  BONUS_CONDITION: "Bonus: {item}, valid with any order",
  BONUS_MIN_ORDER: "Minimum order {ron} RON",
  BONUS_HELD_TITLE: "You have a bonus",
  BONUS_HELD_BODY:
    "Use it by {date} at any participating venue, with an order. The item is the one that venue offers.",
  BONUS_HELD_CAP: "While you hold it, your card fills up to {n} stamps.",
  BONUS_VENUES_TITLE: "Participating venues",
  BONUS_VENUES_EMPTY: "No venue takes part yet. They appear here as they join.",
  BONUS_HONOURS_UNTIL: "Left the program; accepts bonuses until {date}",
  BONUS_NEXT_ITEM: "From {date}: {item}",
  BONUS_USE_HERE: "Use bonus here",
  BONUS_DISTANCE: "{distance} away",
  BONUS_ENABLE_LOCATION: "Enable location to use it",
  BONUS_LAST_USED: "Last bonus used {time} at {venue}",
  BONUS_RULES_LINK: "Bonus rules",
  BONUS_LOADING: "Loading your stamp card",
  BONUS_ERROR: "Couldn't load your stamp card.",
  BONUS_START_TOO_FAR: "You need to be at the venue, within {m} m.",
  BONUS_START_LOW_ACCURACY:
    "Your location isn't precise enough yet (it needs {m} m or better). Try again in a moment.",
  BONUS_START_LOCATION: "Enable location and try again.",
  BONUS_START_VENUE_LIMIT: "This venue has reached today's bonus limit. Try another venue or another day.",
  BONUS_START_NOT_PARTICIPATING: "This venue doesn't accept bonuses right now.",
  BONUS_START_NO_BONUS: "You don't have a bonus right now.",
  BONUS_START_ALREADY_USED: "This bonus is already used.",
  BONUS_ERROR_GENERIC: "Something went wrong. Try again.",
  BONUS_REDEEM_TITLE: "Use bonus",
  BONUS_REDEEM_SHOW: "Show this screen to the staff",
  BONUS_REDEEM_EXPIRES: "Expires in {time}",
  BONUS_REDEEM_LIVE: "Live",
  BONUS_REDEEM_STAFF: "Staff: type the venue PIN to confirm",
  BONUS_PIN_LABEL: "Venue PIN",
  BONUS_PIN_DELETE: "Delete a digit",
  BONUS_PIN_CONFIRM: "Confirm",
  BONUS_PIN_BAD: "Wrong PIN. Tries left: {n}.",
  BONUS_PIN_LOCKED: "Too many wrong PINs. Try again after {time}.",
  BONUS_PIN_UNSET: "This venue hasn't set a PIN yet.",
  BONUS_REDEEM_EXPIRED: "This screen expired.",
  BONUS_REDEEM_RESTART: "Start again",
  BONUS_REDEEM_CANCEL: "Not now",
  BONUS_USED_TITLE: "Used ✓",
  BONUS_USED_AT: "{time} at {venue}",
  BONUS_USED_DONE: "Done",
  BONUS_CHIP_TITLE: "Resto Bonus venue",
  BONUS_CHIP_HINT: "Collect a stamp here with Dine In!",
  BONUS_CHIP_HONOURING: "Accepts bonuses until {date}; no new stamps here.",
  // Manager Bonus tab (/manage; the operator editor reuses the EN strings).
  MGR_TAB_BONUS: "Bonus",
  MGR_BONUS_TITLE: "Resto Bonus",
  MGR_BONUS_DESC:
    "Optional. Diners collect a stamp when they tap Dine In! at a participating venue; a full card earns one bonus item, valid with any order, at any participating venue.",
  MGR_BONUS_RULES_LINK: "Read the Bonus rules",
  MGR_BONUS_UNAVAILABLE: "Bonus isn't available yet.",
  MGR_BONUS_STATUS_ON: "Taking part",
  MGR_BONUS_STATUS_OFF: "Not taking part",
  MGR_BONUS_ENABLE: "Take part in Resto Bonus",
  MGR_BONUS_ENABLE_HINT:
    "Set the item and the PIN first. If you leave, you still accept the bonuses earned before you left, for 30 days.",
  MGR_BONUS_HONOURING: "You left the program. Accept earlier bonuses until {date}.",
  MGR_BONUS_NEEDS_ITEM: "Save the bonus item first.",
  MGR_BONUS_NEEDS_PIN: "Set the staff PIN first.",
  MGR_BONUS_ITEM_TITLE: "Bonus item",
  MGR_BONUS_ITEM_HINT:
    "A standard, cheap, non-alcoholic item. Diners see “Bonus: [item], valid with any order”. While you take part, a change applies 7 days after you save it.",
  MGR_BONUS_ITEM_NAME: "Item name",
  MGR_BONUS_ITEM_NAME_RO: "Romanian name (if different)",
  MGR_BONUS_ITEM_VALUE: "Menu price, RON (at most {cap})",
  MGR_BONUS_MIN_ORDER: "Minimum order, RON (optional)",
  MGR_BONUS_NON_ALCOHOLIC: "I confirm this item contains no alcohol.",
  MGR_BONUS_SAVE_ITEM: "Save item",
  MGR_BONUS_ITEM_SAVED: "Item saved.",
  MGR_BONUS_ITEM_SCHEDULED: "Saved. Diners get 7 days' notice: it applies on {date}.",
  MGR_BONUS_NEXT: "Scheduled for {date}: {item}",
  MGR_BONUS_ERR_ITEM: "The item name needs 2 to 60 characters.",
  MGR_BONUS_ERR_VALUE: "Enter the menu price as a number of RON.",
  MGR_BONUS_ERR_OVER_CAP: "The price can be at most {cap} RON.",
  MGR_BONUS_ERR_MIN_ORDER: "Enter the minimum order as a number of RON (or leave it empty).",
  MGR_BONUS_ERR_NON_ALCOHOLIC: "Confirm the item is non-alcoholic.",
  MGR_BONUS_PIN_TITLE: "Staff PIN",
  MGR_BONUS_PIN_HINT:
    "Staff type it on the diner's phone to confirm a bonus: 4 to 8 digits. A new PIN applies at once. Share it only with your team.",
  MGR_BONUS_PIN_NEW: "New PIN",
  MGR_BONUS_PIN_REPEAT: "Repeat the PIN",
  MGR_BONUS_PIN_SAVE: "Save PIN",
  MGR_BONUS_PIN_SET_AT: "PIN set on {date}",
  MGR_BONUS_PIN_NONE: "No PIN yet",
  MGR_BONUS_PIN_SAVED: "PIN saved.",
  MGR_BONUS_ERR_PIN: "The PIN must be 4 to 8 digits.",
  MGR_BONUS_ERR_PIN_MATCH: "The two PINs don't match.",
  MGR_BONUS_STATS_TITLE: "Last 30 days",
  MGR_BONUS_STAT_STAMPS: "Stamps given here",
  MGR_BONUS_STAT_USED: "Bonuses used here",
  MGR_BONUS_LOG_TITLE: "Bonuses used here",
  MGR_BONUS_LOG_HINT: "Time and item only. Resto never shows who used a bonus.",
  MGR_BONUS_LOG_EMPTY: "None yet.",
  MGR_BONUS_ERR_GENERIC: "Couldn't save. Try again.",

  // --- Manager editor (/manage) — the one bilingual staff surface --------------
  MGR_EYEBROW: "Resto · Venue manager",
  MGR_FALLBACK_VENUE: "Your venue",
  MGR_SUBTITLE: "Your listing, offers and events. Whatever you change here, guests see right away.",
  MGR_SIGNOUT: "Sign out",
  MGR_TABS_ARIA: "Manager sections",
  MGR_TAB_EDITOR: "Editor",
  MGR_TAB_CONVERSIONS: "Conversions",
  MGR_LOADING: "Loading…",
  MGR_ERR_NO_VENUE: "Your account isn't linked to a venue yet. Write to us and we'll sort it out.",
  MGR_ERR_NOT_FOUND: "Venue not found.",
  MGR_ERR_LOAD: "Failed to load",
  MGR_ERR_NAME_REQUIRED: "Name is required.",
  MGR_ERR_TIME_OPEN: "Opening time must be HH:MM.",
  MGR_ERR_TIME_CLOSE: "Closing time must be HH:MM.",
  MGR_ERR_SAVE: "Save failed",
  MGR_SAVED: "Saved.",
  MGR_UNSAVED: "Unsaved changes",
  MGR_SAVE_CTA: "Save changes",
  MGR_PHOTOS_TITLE: "Photos",
  MGR_PHOTOS_DESC: "Your hero image and up to four gallery photos.",
  MGR_PHOTOS_HERO: "Hero image",
  MGR_PHOTOS_GALLERY: "Gallery (up to 4)",
  MGR_IDENTITY_TITLE: "Identity",
  MGR_IDENTITY_DESC: "The name and descriptions guests see. Fill in the (RO) fields for Romanian guests; if you leave them empty, guests see the English text.",
  MGR_FIELD_NAME: "Name *",
  MGR_FIELD_NEIGHBORHOOD: "Neighborhood",
  MGR_PH_NEIGHBORHOOD: "Old Town",
  MGR_FIELD_MAPS: "Google Maps link",
  MGR_CUISINE_LABEL: "Cuisine (pick one or more)",
  MGR_TAGS_LABEL: "Tags: vibe & features",
  MGR_DESC_EN: "Description (EN)",
  MGR_DESC_RO: "Description (RO)",
  MGR_DESC_EN_PH: "What makes this place special: the room, the menu, the vibe.",
  MGR_DESC_RO_PH: "Ce face acest loc special: sala, meniul, atmosfera.",
  MGR_HOURS_TITLE: "Hours",
  MGR_HOURS_DESC: "Used by the app to show open / closed.",
  MGR_OPENS_AT: "Opens at",
  MGR_CLOSES_AT: "Closes at",
  MGR_HOURS_HINT: "Wrap-past-midnight is allowed (e.g. opens 18:00, closes 02:00).",
  MGR_CAPACITY_TITLE: "Capacity display",
  MGR_CAPACITY_DESC: "How guests see your live capacity (enabled for your venue).",
  MGR_CAP_EXACT: "Show exact seats",
  MGR_CAP_DISCREET: "Discreet (no numbers)",
  MGR_CAP_EXACT_HINT: "Guests see the exact number of free seats and a fill bar.",
  MGR_CAP_DISCREET_HINT: "Guests see the busy level and a rough bar, never a number or an empty-looking room.",
  MGR_RES_TITLE: "Reservations",
  MGR_RES_DESC: "Tell guests how booked you are today. It shows as a guide on your card. Resto is not a booking system.",
  MGR_RES_ENABLED: "Accepts reservations",
  MGR_RES_ENABLED_HINT: "Shows a “takes reservations” marker on your venue's card.",
  MGR_RES_STATUS_LABEL: "Today's status",
  MGR_RES_ST_AVAILABLE: "Available",
  MGR_RES_ST_LIMITED: "Limited",
  MGR_RES_ST_FULL: "Fully booked",
  MGR_RES_STATUS_HINT: "A guide only: tomorrow it goes back to the neutral marker. Select today's value again to refresh it.",
  MGR_POS_TITLE: "POS connection",
  MGR_POS_DESC: "Your venue's live occupancy feed. We set up and look after the connection; there is nothing for you to do here.",
  MGR_POS_STATUS_PENDING: "Pending",
  MGR_POS_STATUS_ACTIVE: "Active",
  MGR_POS_STATUS_ERROR: "Sync issue",
  MGR_POS_LAST_SYNC: "Last sync: {time}",
  MGR_POS_NEVER: "Not synced yet",
  MGR_POS_HINT_PENDING: "The connection is set up and waiting for its first successful sync.",
  MGR_POS_HINT_ACTIVE: "Table occupancy flows automatically from your POS. Nothing to do here.",
  MGR_POS_HINT_ERROR: "The last sync failed; it retries on its own. If this keeps happening, write to us.",
  MGR_EVENT_TITLE: "What's on",
  MGR_EVENT_DESC:
    "One short announcement shown in the live ticker (e.g. “Live jazz tonight”). You can have one at a time.",
  MGR_EVENT_MESSAGE: "Message",
  MGR_EVENT_PH: "Live jazz tonight @ 8PM",
  MGR_EVENT_TYPE: "Type",
  MGR_EVENT_UPDATE: "Update event",
  MGR_EVENT_PUBLISH: "Publish event",
  MGR_EVENT_REMOVE: "Remove",
  MGR_EVENT_EMPTY: "Write something first, or clear the event.",
  MGR_EVENT_PUBLISHED: "Event published.",
  MGR_EVENT_SAVE_ERR: "Could not save the event.",
  MGR_EVENT_REMOVED: "Event removed.",
  MGR_EVENT_REMOVE_ERR: "Could not remove the event.",
  MGR_KIND_ANNOUNCEMENT: "Announcement",
  MGR_KIND_PROMO: "Promo",
  MGR_KIND_LIVE: "Live",
  MGR_KIND_MENU: "Menu",
  MGR_KIND_INFO: "Info",
  MGR_OFFERS_TITLE: "Offers & events",
  MGR_OFFERS_DESC: "Deals and special events shown on your card. Saved as you edit.",
  // AI copy helper (It-8) — offered on the event message and the offer line.
  // It only re-words what you typed; it never adds an offer of its own.
  MGR_AI_POLISH: "Improve with AI",
  MGR_AI_POLISH_BUSY: "Working on it…",
  MGR_AI_PICK_HINT: "One field, one language: pick the version your guests read.",
  MGR_AI_DEMO_TITLE: "Demo text",
  MGR_AI_DEMO_BODY:
    "no AI provider is set up yet, so these are sample lines, not your offer.",
  MGR_AI_GENERATED: "Generated with AI. Check it before saving.",
  MGR_AI_ERR_AUTH: "Your session expired. Sign in again.",
  MGR_AI_ERR_RATE: "Too many requests in a row. Wait a minute.",
  MGR_AI_ERR_UNAVAILABLE: "The AI helper isn't available right now.",
  MGR_AI_ERR_NETWORK: "Connection problem. Try again.",
  MGR_AI_ERR_UNUSABLE: "Nothing usable came back. Try rewording the line.",
  MGR_CONV_TITLE: "Conversions",
  MGR_CONV_DESC: "Guests Resto sent to your venue in the last 30 days. Read it as a minimum, not an exact count: visitors we can't identify in the app aren't counted, so the real number may be a little higher. Feedback is private to you.",
  MGR_CONV_UNAVAILABLE: "Conversion data isn't available yet.",
  MGR_STAT_CONVERSIONS: "Conversions",
  MGR_STAT_FEEDBACK: "Feedback",
  MGR_STAT_RATING: "Avg rating",
  MGR_STAT_SPEND: "Avg spend",
  // Shared editor chrome on /manage (uploaders, offers editor) — It-3.
  MGR_UPLOAD_UPLOADING: "Uploading…",
  MGR_UPLOAD_DROP: "Drop an image here or tap to browse",
  MGR_UPLOAD_REPLACE: "Drop a new image or tap to replace",
  MGR_UPLOAD_REMOVE: "Remove",
  MGR_UPLOAD_REMOVE_ARIA: "Remove image",
  MGR_UPLOAD_ADD: "Add",
  MGR_UPLOAD_MAX: "max {mb} MB",
  MGR_UPLOAD_MAX_EACH: "max {mb} MB each",
  MGR_UPLOAD_ERR_TOO_LARGE: "This image is over the {mb} MB limit.",
  MGR_UPLOAD_ERR_TYPE: "Unsupported file type. Use JPG, PNG, WebP or AVIF.",
  MGR_UPLOAD_ERR: "Upload failed",
  MGR_OFFER_LOAD_ERR: "Couldn't load your offers",
  MGR_OFFER_EMPTY: "No offers or events yet. Add one below.",
  MGR_OFFER_ADD_TITLE: "Add an offer or special event",
  MGR_OFFER_INFO_PH: "e.g. 2-for-1 cocktails · Live jazz tonight · 20% off mains",
  MGR_OFFER_DESC_PH: "Optional details shown when a guest taps the offer…",
  MGR_OFFER_ROW_PH: "Describe the offer or event…",
  MGR_OFFER_STARTS: "Starts",
  MGR_OFFER_ENDS: "Ends",
  MGR_OFFER_DAYS: "Days (none = every day)",
  MGR_DAY_SUN: "Sun",
  MGR_DAY_MON: "Mon",
  MGR_DAY_TUE: "Tue",
  MGR_DAY_WED: "Wed",
  MGR_DAY_THU: "Thu",
  MGR_DAY_FRI: "Fri",
  MGR_DAY_SAT: "Sat",
  MGR_OFFER_REPEATS_WEEKLY: "Repeats weekly",
  MGR_OFFER_ONE_TIME_HINT: "One-time: runs once on the next matching day, then deletes itself.",
  MGR_OFFER_ADD: "Add",
  MGR_OFFER_REPEATS: "Repeats",
  MGR_OFFER_ACTIVE: "Active",
  MGR_OFFER_IMAGE: "Image (optional)",
  MGR_OFFER_ERR_INFO: "Write a short line about the offer first.",
  MGR_OFFER_ERR_TIME: "Times must be HH:MM.",
  MGR_OFFER_ERR_ADD: "Couldn't add the offer",
  MGR_OFFER_ERR_UPDATE: "Couldn't save the change",
  MGR_OFFER_ERR_DELETE: "Couldn't delete the offer",
  MGR_OFFER_DELETE_TITLE: "Delete entry #{n}?",
  MGR_OFFER_DELETE_TITLE_PLAIN: "Delete this entry?",
  MGR_OFFER_DELETE_DESC: "“{info}” will be removed. The remaining entries renumber automatically.",
  MGR_OFFER_NO_DESC: "(no description)",
  MGR_OFFER_DELETE: "Delete",
  MGR_OFFER_DELETE_ARIA: "Delete entry #{n}",
  MGR_OFFER_DELETE_ARIA_PLAIN: "Delete entry",
  MGR_CANCEL: "Cancel",
} as const;

const ro: Record<keyof typeof en, string> = {
  WELCOME_TITLE: "Bun venit",
  WELCOME_SUBTITLE: "Alege orașul ca să vezi ce e deschis în seara asta.",
  WELCOME_CITY_PLACEHOLDER: "Selectează orașul",
  WELCOME_CITY_SOON: "În curând",
  WELCOME_MORE_CITIES_SOON: "Mai multe orașe în curând. Caută-l pe al tău.",
  WELCOME_CITY_SEARCH: "Caută orașul…",
  WELCOME_CITY_NONE: "Niciun oraș găsit",
  WELCOME_CITY_NEAREST: "Cel mai aproape",
  WELCOME_CITY_VENUE: "local",
  WELCOME_CITY_VENUES: "localuri",
  WELCOME_CONTINUE: "Continuă",
  WELCOME_CITIES_ERROR: "Nu am putut încărca orașele. Încearcă din nou.",

  RETRY: "Încearcă din nou",
  CLOSE: "Închide",
  SCROLL_LEFT: "Derulează la stânga",
  SCROLL_RIGHT: "Derulează la dreapta",
  TICKER_LABEL: "Bandă live de evenimente",
  MAP_CLUSTER: "{n} localuri",
  DETAIL_GO_IMAGE: "Mergi la imaginea {n}",

  HOME_TITLE: "În seara asta lângă tine",
  HOME_TITLE_DAY: "Azi lângă tine",
  HOME_SUBTITLE: "Ce e deschis, ce oferte sunt și ce se întâmplă în seara asta, direct de la fiecare local.",
  HOME_CHANGE_CITY: "Schimbă orașul",
  SHOWCASE_DEMO_PILL: "Demo · date simulate",
  SHOWCASE_SPEED_LABEL: "Viteza simulării {speed}×, atinge pentru a schimba",
  HOME_CITY_EMPTY: "Niciun local în {city} încă. Revino curând.",
  HOME_LOAD_ERROR: "Nu am putut încărca localurile. Verifică conexiunea și încearcă din nou.",
  HOME_TAB_RECOMMENDED: "Recomandate",
  HOME_TAB_OFFERS: "Oferte & Evenimente",

  HOME_SEARCH_PLACEHOLDER: "Caută localuri, bucătării, zone…",
  HOME_SEARCH_CLEAR: "Șterge căutarea",
  HOME_FILTERS_RECOMMENDED: "Populare",
  HOME_FILTERS_CUSTOM: "Filtre",
  HOME_FILTERS_TITLE: "Filtre personalizate",
  HOME_FILTERS_CLEAR: "Șterge tot",
  HOME_FILTERS_CUISINES: "Bucătării",
  HOME_FILTERS_TAGS: "Etichete",
  HOME_FILTERS_SHOW: "Arată {n} localuri",
  HOME_FILTERS_NONE: "Niciun local nu se potrivește filtrelor.",
  HOME_RESULT_COUNT: "{n} localuri",
  HOME_RESULT_COUNT_ONE: "1 local",

  HOME_FILTER_OPEN_NOW: "Deschis acum",
  HOME_FILTER_DISCOUNT: "Cu ofertă activă",
  HOME_FILTER_QUICK: "Locuri rapide",
  HOME_FILTER_QUIET: "Fără aglomerație",
  HOME_FILTER_SOME_SPACE: "Mai are locuri",
  HOME_FILTER_NEARBY: "La max. {km} km",

  AI_ASK_PILL: "Întreabă Resto",
  AI_ASK_HINT: "Transformă în filtre",
  AI_ASK_PENDING: "Lucrez la asta…",
  AI_ASK_MISSED: "Nu am putut transforma asta în filtre, așa că am căutat ca text.",

  RECS_TITLE: "Recomandate pentru tine",
  RECS_SUBTEXT: "Pe baza vizitelor și evaluărilor tale de aici",
  QUICK_TITLE: "Vrei ceva rapid?",
  QUICK_SUBTEXT: "Locuri rapide, fără pretenții, când nu ai timp de pierdut.",
  NEARBY_TITLE: "Cel mai aproape de tine",
  NEARBY_ENABLE: "Vezi ce e cel mai aproape",
  NEARBY_ENABLE_HINT: "Folosește locația ta",
  NEARBY_LOADING: "Căutăm ce e aproape de tine…",
  NEARBY_NONE: "Niciun restaurant deschis lângă tine chiar acum.",
  NEARBY_DENIED: "Locația e dezactivată. Activeaz-o ca să vezi ce e cel mai aproape.",
  NEARBY_UNAVAILABLE: "Locația nu e disponibilă momentan.",
  NEARBY_RETRY: "Activează locația",
  NEARBY_DENIED_HINT: "Dacă nu apare nicio fereastră, permite locația pentru acest site din setările browserului.",
  NEARBY_TIMEOUT: "Nu am putut obține locația. Încearcă din nou.",
  NEARBY_UNSUPPORTED: "Acest browser nu suportă localizarea.",
  NEARBY_INSECURE: "Locația necesită o conexiune securizată (https).",

  MAP_OPEN: "Hartă",
  MAP_TITLE: "Harta live",
  MAP_RECENTER: "Centrează pe mine",
  MAP_YOU: "Ești aici",

  OFFERS_EMPTY: "Nicio ofertă în {city} momentan.",
  OFFERS_SECTION: "Oferte & evenimente",
  OFFER_LIVE: "Acum",
  OFFER_EVERY_DAY: "Zilnic",
  OFFER_VIEW_VENUE: "Vezi localul",

  LEVEL_GREEN: "Locuri libere",
  LEVEL_YELLOW: "Se umple",
  LEVEL_RED: "Plin",
  LEVEL_AVAILABLE: "Disponibil",

  CARD_CLOSED: "Închis",
  CARD_OPEN: "Deschis",
  CARD_OPEN_UNTIL: "Deschis · până la {time}",
  CARD_OPEN_UNTIL_SHORT: "până la {time}",
  CARD_CLOSED_OPENS: "Închis · deschide la {time}",
  CARD_SEATS_AVAILABLE: "din {total} locuri disponibile",

  DETAIL_PREV_IMAGE: "Poza anterioară",
  DETAIL_NEXT_IMAGE: "Poza următoare",
  DETAIL_MAP_TITLE: "Deschide în Google Maps",
  DETAIL_MAP_SUBTITLE: "Vezi locația",

  RES_MARKER_NEUTRAL: "Acceptă rezervări",
  RES_STATUS_AVAILABLE: "Rezervări disponibile",
  RES_STATUS_LIMITED: "Rezervări limitate",
  RES_STATUS_FULL: "Complet rezervat",
  RES_DETAIL_TITLE: "Rezervări",
  RES_DETAIL_NEUTRAL: "Acest local acceptă rezervări.",
  RES_DETAIL_HINT: "Reper orientativ setat de local pentru azi. Pentru rezervare, contactează localul.",

  GOLIVE_CONFIRM_TITLE: "Gata să devii live?",
  GOLIVE_CONFIRM_SUB: "Apasă mai jos pentru a face restaurantul vizibil pentru toată lumea.",
  GOLIVE_CONFIRM_CTA: "Activează",
  GOLIVE_ACTIVATING: "Se activează…",
  GOLIVE_SUCCESS: "{name} este acum live.",
  GOLIVE_SUCCESS_SUB: "Restaurantul este acum vizibil pentru toată lumea. Bun venit pe Resto.",
  GOLIVE_INVALID_TITLE: "Acest link nu mai este valid",
  GOLIVE_INVALID_SUB: "Este posibil să fi fost deja folosit. Cere un link nou pentru a deveni live.",
  GOLIVE_OPEN_APP: "Deschide aplicația",

  LANG_TOGGLE_TO_EN: "Schimbă în engleză",
  LANG_TOGGLE_TO_RO: "Schimbă în română",

  // ---- Marketing landing (/landing) ----
  LANDING_NAV_OPEN_APP: "Deschide aplicația",

  LANDING_TICKER_ARIA: "Exemple de actualizări de la localuri",
  LANDING_TICKER_EXAMPLE: "Exemplu",
  LANDING_TICKER_OPEN_NOW: "deschis · mese libere acum",
  LANDING_TICKER_FILLING: "deschis · se umple",
  LANDING_TICKER_SEATS_LEFT: "deschis · {n} locuri rămase",
  LANDING_TICKER_EVENT: "concert diseară · mese libere",

  LANDING_HERO_TITLE_A: "Intră",
  LANDING_HERO_TITLE_B: "acum.",


  LANDING_RESTO_NOT_RES_TITLE: "Nu e un sistem de rezervări",
  LANDING_RESTO_NOT_RES_BODY: "Platformele de rezervări vând vinerea viitoare. Noi vindem următoarele 30–60 de minute: oameni deja ieșiți în oraș, deja flămânzi, deja aproape de dvs.",
  LANDING_RESTO_NEVER_EMPTY_TITLE: "Nu apari niciodată gol",
  LANDING_RESTO_NEVER_EMPTY_BODY: "Feedul spune un singur lucru despre dvs.: deschis, cu loc acum. O sală care pare vie și mai are o masă atrage oamenii. Nu anunțăm niciodată o seară slabă.",

  LANDING_COMPARE_TITLE: "Cum se compară Resto",
  LANDING_COMPARE_ROW_MOMENT: "Momentul",
  LANDING_COMPARE_ROW_DATA: "Datele",
  LANDING_COMPARE_ROW_EVENTS: "Evenimente & oferte",
  LANDING_COMPARE_ROW_FEE: "Taxă per cuvert",
  LANDING_COMPARE_ROW_WALKINS: "Aduce clienți acum",
  LANDING_COMPARE_MOMENT_RESTO: "Următoarele 30–60 min",
  LANDING_COMPARE_MOMENT_FUTURE: "Rezervare în viitor",
  LANDING_COMPARE_MOMENT_PASSIVE: "Listare pasivă",
  LANDING_COMPARE_DATA_LIVE: "Live din POS",
  LANDING_COMPARE_DATA_MANUAL: "Manual",
  LANDING_COMPARE_DATA_CROWD: "Estimate",
  LANDING_COMPARE_EVENTS_RESTO: "Da, live, chiar acum",
  LANDING_COMPARE_EVENTS_BOOKING: "Da, la rezervare",
  LANDING_COMPARE_EVENTS_LIMITED: "Limitat",
  LANDING_COMPARE_FEE_RESTO: "Nu, €50 fix",
  LANDING_COMPARE_FEE_OT: "Da ($1–1,50 / €2,60)",
  LANDING_COMPARE_FEE_FREE: "Gratuit",
  LANDING_COMPARE_YES: "Da",
  LANDING_COMPARE_NO: "Nu",


  LANDING_PEOPLE_PROBLEM: "Vrei să mănânci în oraș acum, dar nu ai de unde să știi cine are o masă liberă. Așa că suni pe rând, te plimbi sau renunți și comanzi acasă. Resto îți arată live.",
  LANDING_PEOPLE_STEP1_TITLE: "Deschide feedul",
  LANDING_PEOPLE_STEP1_BODY: "Toate localurile din orașul tău, întâi cele deschise.",
  LANDING_PEOPLE_STEP2_TITLE: "Vezi situația",
  LANDING_PEOPLE_STEP2_BODY: "Cât de plin e, în trei culori. Ce se întâmplă diseară. Ofertele active.",
  LANDING_PEOPLE_STEP3_TITLE: "Apasă și du-te",
  LANDING_PEOPLE_STEP3_BODY: "O apăsare deschide navigația. În douăzeci de minute ești la masă.",
  LANDING_PEOPLE_NO_LOGIN: "Explorezi fără cont. Gradul de ocupare live cere un cont gratuit. În engleză sau română.",

  LANDING_DEMO_PCT_FULL: "{n}% plin",
  LANDING_DEMO_PILL_GREEN: "deschis · mese libere acum",
  LANDING_DEMO_PILL_YELLOW: "deschis · se umple",
  LANDING_DEMO_PILL_RED: "plin momentan",
  LANDING_DEMO_TAG_VATRA: "Cuptor cu lemne · Mănăștur",
  LANDING_DEMO_TAG_SAMSARA: "Vegetarian · Centru",
  LANDING_DEMO_TAG_KAISEKI: "Omakase · Andrei Mureșanu",
  LANDING_DEMO_TAG_MOSAIC: "Levantin · Piața Muzeului",

  LANDING_FAQ_TITLE: "Răspunsuri oneste.",
  LANDING_FAQ_Q1: "Prin ce diferă de OpenTable sau de altă platformă de rezervări?",
  LANDING_FAQ_Q2: "De unde vin datele despre capacitate?",
  LANDING_FAQ_A2: "Din sistemul de casă (POS) al localului. Citim ce mese sunt ocupate, cam o dată la 90 de secunde, iar nivelul de pe card se actualizează.",
  LANDING_FAQ_Q3: "Cât costă Resto?",
  LANDING_FAQ_Q4: "Ce informații mai sunt afișate?",
  LANDING_FAQ_A4: "Programul, ofertele și evenimentele fiecărui local. Cu timpul vom adăuga și altele.",
  LANDING_FOOTER_COPYRIGHT: "© Resto {year}",

  // --- Account sidebar -------------------------------------------------------
  ACCOUNT_BUTTON: "Cont și setări",
  SIDEBAR_TITLE: "Cont și setări",
  SIDEBAR_GUEST_TITLE: "Navighezi ca oaspete",
  SIDEBAR_GUEST_SUB: "Feedul merge și fără cont. Un cont gratuit adaugă gradul de ocupare live, toate ofertele și recomandări pe baza vizitelor tale.",
  SIDEBAR_SIGNIN_CTA: "Intră în cont / Creează cont",
  SIDEBAR_SECTION_ACCOUNT: "Cont",
  SIDEBAR_SECTION_SETTINGS: "Setări",
  SIDEBAR_DISPLAY_NAME: "Nume afișat",
  SIDEBAR_SAVE: "Salvează",
  SIDEBAR_SAVED: "Salvat.",
  SIDEBAR_EMAIL: "Email",
  SIDEBAR_EMAIL_VERIFIED: "Verificat",
  SIDEBAR_EMAIL_UNVERIFIED: "Neverificat",
  SIDEBAR_PROVIDER_GOOGLE: "Conectat cu Google",
  SIDEBAR_PROVIDER_EMAIL: "Cont cu email și parolă",
  SIDEBAR_CHANGE_PASSWORD: "Schimbă parola",
  SIDEBAR_PASSWORD_UPDATED: "Parola a fost schimbată.",
  SIDEBAR_SIGNOUT: "Ieși din cont",
  SIDEBAR_DELETE_ACCOUNT: "Șterge contul",
  SIDEBAR_DELETE_CONFIRM_TITLE: "Îți ștergi contul?",
  SIDEBAR_DELETE_CONFIRM_BODY:
    "Contul și datele legate de el se șterg definitiv. Aplicația merge în continuare și fără cont.",
  SIDEBAR_DELETE_CONFIRM_CTA: "Șterge definitiv",
  SIDEBAR_CANCEL: "Renunță",
  SIDEBAR_LANGUAGE: "Limbă",
  SIDEBAR_THEME: "Temă",
  THEME_DARK: "Întunecată",
  THEME_LIGHT: "Luminoasă",
  THEME_TOGGLE_TO_DARK: "Treci pe tema întunecată",
  THEME_TOGGLE_TO_LIGHT: "Treci pe tema luminoasă",
  SIDEBAR_LOCATION: "Locație",
  SIDEBAR_LOCATION_ON: "Activată",
  SIDEBAR_LOCATION_OFF: "Dezactivată",
  SIDEBAR_LOCATION_PROMPTING: "Așteptăm permisiunea…",
  SIDEBAR_LOCATION_DENIED: "Blocată din setările browserului",
  SIDEBAR_LOCATION_UNAVAILABLE: "Indisponibilă pe acest dispozitiv",
  SIDEBAR_LOCATION_ENABLE: "Activează",
  SIDEBAR_CLOSE: "Închide meniul",
  SIDEBAR_STAFF_MANAGE: "Administrează localul",
  SIDEBAR_STAFF_ADMIN: "Panoul de operator",
  SIDEBAR_VERSION: "Versiunea {v}",
  SIDEBAR_TERMS: "Termeni",
  SIDEBAR_PRIVACY: "Confidențialitate",

  // --- Consumer auth (sidebar views) ----------------------------------------
  AUTH_SIGNIN_TITLE: "Intră în cont",
  AUTH_SIGNUP_TITLE: "Creează cont",
  AUTH_EMAIL_LABEL: "Email",
  AUTH_PASSWORD_LABEL: "Parolă",
  AUTH_NEW_PASSWORD_LABEL: "Parolă nouă",
  AUTH_NAME_LABEL: "Nume",
  AUTH_NAME_HINT: "Apare în meniul contului. Merge orice nume.",
  AUTH_PW_RULE: "Minim 8 caractere, cu o literă și o cifră",
  AUTH_SIGNIN_CTA: "Intră",
  AUTH_SIGNUP_CTA: "Creează contul",
  AUTH_GOOGLE_CTA: "Continuă cu Google",
  AUTH_APPLE_CTA: "Continuă cu Apple",
  AUTH_OR: "sau",
  AUTH_TO_SIGNUP: "Ești nou aici? Creează un cont",
  AUTH_TO_SIGNIN: "Ai deja cont? Intră",
  AUTH_FORGOT: "Ți-ai uitat parola?",
  AUTH_FORGOT_TITLE: "Resetează parola",
  AUTH_FORGOT_CTA: "Trimite linkul de resetare",
  AUTH_FORGOT_SENT:
    "Dacă există un cont pentru acest email, linkul de resetare e pe drum. Deschide-l pe acest dispozitiv.",
  AUTH_RESET_TITLE: "Setează o parolă nouă",
  AUTH_RESET_CTA: "Salvează parola nouă",
  AUTH_RESET_DONE: "Parola a fost actualizată.",
  AUTH_CONFIRM_TITLE: "Confirmă-ți emailul",
  AUTH_CONFIRM_BODY: "Ți-am trimis un link de confirmare la {email}. Deschide-l, apoi intră în cont.",
  AUTH_CONFIRM_RESEND: "Retrimite emailul",
  AUTH_CONFIRM_RESENT: "Trimis.",
  AUTH_ERR_INVALID: "Email sau parolă greșite.",
  AUTH_ERR_EMAIL_TAKEN: "Emailul nu poate fi folosit. Încearcă să intri în cont.",
  AUTH_ERR_WEAK_PASSWORD: "Parola trebuie să aibă minim 8 caractere.",
  AUTH_ERR_UNCONFIRMED: "Confirmă-ți mai întâi emailul. Verifică inboxul.",
  AUTH_ERR_RATE_LIMIT: "Prea multe încercări. Așteaptă un minut și încearcă din nou.",
  AUTH_ERR_CAPTCHA: "Verificarea a eșuat. Te rugăm să încerci din nou.",
  AUTH_COOLDOWN_WAIT: "Prea multe încercări. Așteaptă {seconds}s.",
  AUTH_ERR_GENERIC: "Ceva nu a mers. Încearcă din nou.",

  // --- Anonymous-ID consent notice -------------------------------------------
  CONSENT_ARIA: "Notă de confidențialitate",
  CONSENT_BODY: "Putem reține acest dispozitiv printr-un ID anonim? Ne ajută să numărăm vizitele fără cont. Fără reclame.",
  CONSENT_ACCEPT: "Permite",
  CONSENT_DECLINE: "Nu, mulțumesc",
  CONSENT_MORE: "Detalii",

  // --- Conversion: Dine In check-in + feedback --------------------------------
  DINE_IN_CTA: "Sunt aici!",
  DINE_IN_INFO_LABEL: "Ce face acest buton?",
  DINE_IN_EXPLAINER:
    "Verifică dacă ești la cel mult 75 m, apoi reține doar localul și data de azi. Poziția ta nu e salvată. Într-un local Bonus Resto, dacă ești conectat, primești și o ștampilă. Vizitele automate rămân oprite dacă nu le pornești din Setări.",
  DINE_IN_ENABLE_LOCATION: "Activează locația pentru check-in",
  DINE_IN_DONE: "Vizită înregistrată",
  DINE_IN_LEAVE_FEEDBACK: "Lasă un feedback",
  DINE_IN_ERROR: "Check-in-ul nu a reușit. Încearcă din nou.",
  DINE_IN_STAMP_EARNED: "Ștampila {n} din {total}",
  DINE_IN_STAMP_MINTED: "Card plin: ai un bonus!",
  DINE_IN_STAMP_LOW_ACCURACY:
    "Încă nicio ștampilă: locația ta e ±{m} m, iar o ștampilă cere {max} m sau mai bine. Încearcă din nou peste câteva clipe.",
  DINE_IN_STAMP_TOO_NEW: "Ștampilele încep la {hours} de ore după ce îți creezi contul.",
  DINE_IN_STAMP_DAILY: "Ai deja ștampila de azi. Revino mâine.",
  DINE_IN_STAMP_ALREADY: "Vizita aceasta are deja ștampila.",
  DINE_IN_STAMP_CAPPED: "Cardul e plin până folosești bonusul.",
  DINE_IN_STAMP_FROZEN: "Ștampilele sunt oprite pe acest dispozitiv. Scrie-ne dacă e o greșeală.",
  DINE_IN_STAMP_TOO_FAST: "Nicio ștampilă: e prea curând după o ștampilă într-un local departe de aici.",
  DINE_IN_OPEN_CARD: "Cardul",
  GEO_VISIT_TOAST: "Ți-am notat vizita la {venue}.",
  FEEDBACKS_TITLE: "Vizitele tale",
  FEEDBACKS_ENTRY: "Vizite și feedback",
  FEEDBACKS_EMPTY: "Nicio vizită încă. Fă check-in la un restaurant ca să lași feedback.",
  FEEDBACK_STAR: "{n} stele",
  FEEDBACK_RATING_LABEL: "Evaluarea ta",
  FEEDBACK_STARS_HINT: "Atinge o stea pentru a evalua",
  FEEDBACK_SPEND_LABEL: "Cam cât ai cheltuit de persoană (RON)",
  FEEDBACK_SPEND_PLACEHOLDER: "ex. 120",
  FEEDBACK_MESSAGE_LABEL: "Feedbackul tău (opțional)",
  FEEDBACK_MESSAGE_PLACEHOLDER: "Cum a fost?",
  FEEDBACK_SUBMIT: "Trimite",
  FEEDBACK_SKIP: "Mai târziu",
  FEEDBACK_ERROR: "Nu s-a putut salva. Încearcă din nou.",
  FEEDBACK_LEAVE: "Lasă feedback",
  FEEDBACK_EDIT: "Editează",
  FEEDBACK_SPEND_SUMMARY: "{amount} RON/persoană",
  RATING_PROMPT_TITLE: "Evaluează vizita",
  RATING_PROMPT_BODY: "Cum a fost la {venue}?",

  // --- Account-gated surfaces (guest → sign-up conversion) ---------------------
  OFFERS_LOCKED_TITLE: "Ofertele, toate într-un loc",
  OFFERS_LOCKED_BODY: "Creează-ți un cont gratuit ca să vezi toate ofertele și evenimentele din {city}.",
  OFFERS_LOCKED_TAB_HINT: "Necesită cont gratuit",
  SIGNUP_FREE_CTA: "Creează cont gratuit",
  SIGNUP_NUDGE_TITLE: "Mai mult, cu un cont gratuit",
  SIGNUP_NUDGE_BODY: "Vezi disponibilitatea live la localurile conectate, deschizi tabul Oferte și îți păstrezi vizitele și feedbackul. Gratuit.",

  // --- Legal pages (It-6: Art. 13 privacy policy + terms; RO is binding) ---------
  LEGAL_BACK: "Înapoi la aplicație",
  LEGAL_UPDATED: "Ultima actualizare: octombrie 2026",
  LEGAL_BINDING: "Textul în limba română este versiunea care se aplică.",
  LEGAL_AND: "și",
  LEGAL_IDENTITY: "Operat de {controllers}",
  LEGAL_TERMS_TITLE: "Termeni de utilizare",
  LEGAL_TERMS_INTRO:
    "Acești termeni se aplică atunci când folosești Resto (restonow.app). Când îți faci un cont, îi accepți.",
  LEGAL_T1_H: "Serviciul",
  LEGAL_T1_P:
    "Resto arată restaurante din orașe din România, cu disponibilitate live acolo unde localul o pune la dispoziție, plus oferte și evenimente. Navigarea printre localuri e gratuită; disponibilitatea live și tabul Oferte cer un cont gratuit. Bonus Resto, un card de ștampile în localurile participante, are propriul regulament (restonow.app/bonus-rules), care face parte din acești termeni. Resto e în fază-pilot, așa că funcțiile se pot schimba.",
  LEGAL_T2_H: "Contul tău",
  LEGAL_T2_P:
    "Trebuie să ai cel puțin 16 ani ca să îți faci cont. Păstrează-ți parola în siguranță; răspunzi pentru ce se întâmplă în contul tău. Îl poți șterge oricând din meniul contului.",
  LEGAL_T3_H: "Informațiile despre localuri",
  LEGAL_T3_P:
    "Disponibilitatea, evenimentele, ofertele și programul vin de la localuri sau din sistemele lor POS. Le afișăm cu bună-credință, dar nu le putem garanta, iar o masă liberă poate fi ocupată între aplicație și ușă. Ofertele sunt onorate de local, în condițiile localului.",
  LEGAL_T4_H: "Feedbackul tău",
  LEGAL_T4_P:
    "Feedbackul pe care îl lași (stele, suma, o notă) rămâne privat. Ne permiți să îl folosim pentru a opera Resto: să arătăm localurilor cifre agregate, fără identitatea ta, să generăm cu AI rezumate ale temelor recurente pentru echipa noastră și să îmbunătățim serviciul. Nu posta nimic ilegal sau abuziv și nici datele personale ale altor oameni.",
  LEGAL_T5_H: "Utilizare corectă",
  LEGAL_T5_P:
    "Fără scraping la scară mare (API-ul public și endpointul MCP sunt deschise, în limitele lor), fără umblat la conturi care nu sunt ale tale, fără încercări de a perturba platforma, fără check-in-uri sau feedback false. Numele de utilizator nu are voie să imite pe altcineva sau să fie jignitor; putem schimba sau retrage un astfel de nume. Putem suspenda conturile care încalcă acești termeni.",
  LEGAL_T6_H: "Raportări și moderare",
  LEGAL_T6_P1:
    "Punctul nostru unic de contact pentru utilizatori și autorități (Regulamentul UE privind serviciile digitale, art. 11–12) este {email}, în română sau engleză.",
  LEGAL_T6_P2:
    "Ca să raportezi un conținut ilegal sau greșit (un local, ofertele sau evenimentele lui), apasă Raportează pe local sau pe ofertă și trimite formularul scurt din contul tău ori scrie-ne. Spune-ne ce și unde, de ce e o problemă și confirmă că raportezi cu bună-credință; cu un cont, știm deja emailul tău. Fiecare raportare e analizată de un om. Dacă eliminăm sau restricționăm un conținut, îi spunem celui afectat de ce și cum poate contesta decizia.",
  LEGAL_T7_H: "Răspundere",
  LEGAL_T7_P:
    "Resto este oferit ca atare în faza-pilot. În limitele permise de lege, nu răspundem pentru deciziile pe care le iei pe baza informațiilor afișate sau pentru ce se întâmplă într-un local. Nimic din acești termeni nu îți limitează drepturile de consumator.",
  LEGAL_T8_H: "Legea aplicabilă și limba",
  LEGAL_T8_P:
    "Se aplică legea română. Litigiile sunt soluționate de instanțele competente din România, fără a-ți afecta drepturile de consumator. Dacă textele în engleză și în română diferă, prevalează textul în română.",
  LEGAL_T9_H: "Contact",
  LEGAL_T9_P: "Resto este operat de {controllers}. Întrebări: {email}.",
  LEGAL_PRIVACY_TITLE: "Politica de confidențialitate",
  LEGAL_PRIVACY_INTRO:
    "Această politică explică ce date personale prelucrează Resto (restonow.app), de ce, pe ce temei legal, cât timp, cine ne ajută și ce drepturi ai.",
  LEGAL_P1_H: "Cine suntem",
  LEGAL_P1_P:
    "Resto este operat de {controllers}, persoane fizice care decid împreună cum sunt folosite datele tale (operatori asociați), până la înființarea firmei. Pentru orice întrebare sau cerere legată de date, scrie-ne la {email}.",
  LEGAL_P2_H: "Ce date prelucrăm și de ce",
  LEGAL_P2_L1: "Evenimente de utilizare. Când folosești Resto înregistrăm evenimente (vizualizări de pagini și de localuri, apăsări pe Indicații și pe Distribuie) cu orașul și ecranul. Nu conțin un identificator decât dacă ai permis ID-ul anonim de dispozitiv sau ești autentificat. Scop: să înțelegem cum e folosit Resto și să-l îmbunătățim. Temei: interesul nostru legitim (art. 6 alin. (1) lit. f) GDPR). Păstrare: 180 de zile.",
  LEGAL_P2_L2:
    "ID anonim de dispozitiv. Doar dacă apeși Permite: un ID aleatoriu stocat pe dispozitivul tău, care leagă evenimentele și vizitele tale fără cont și le asociază contului, dacă îți faci unul. Temei: consimțământul tău (art. 6 alin. (1) lit. a) GDPR; Legea 506/2004). Îl poți retrage oricând: Setări → Uită acest dispozitiv.",
  LEGAL_P2_L3:
    "Contul tău. Emailul, numele afișat, parola (păstrată doar ca hash de furnizorul nostru de autentificare) sau autentificarea cu Google, limba, tema și confirmarea că ai cel puțin 16 ani. Temei: contractul cu tine, adică Termenii noștri (art. 6 alin. (1) lit. b) GDPR). Păstrare: până îți ștergi contul.",
  LEGAL_P2_L4:
    "Vizite și feedback. Când apeși Sunt aici! sau ai vizitele automate pornite, înregistrăm localul și ziua. Feedbackul pe care îl adaugi (stele, suma aproximativă, o notă opțională) se atașează vizitei. Temei: contractul, pentru funcțiile pe care alegi să le folosești (art. 6 alin. (1) lit. b) GDPR). Păstrare: 24 de luni; după aceea rămân doar totaluri lunare pe local, fără legătură cu tine.",
  LEGAL_P2_L5:
    "Securitate. Un captcha pe formularele de autentificare și înregistrare, limite de cereri pe adresa IP (ținute în memorie câteva minute) și jurnale de server. Temei: interesul nostru legitim de a menține Resto în siguranță (art. 6 alin. (1) lit. f) GDPR).",
  LEGAL_P2_L6:
    "Prieteni. Dacă folosești Prieteni: numele tău de utilizator, cererile pe care le trimiți și le primești, prietenii tăi, persoanele pe care le blochezi și linkul tău de invitație. Prietenii tăi și oricine îți deschide linkul de invitație văd doar numele afișat și numele de utilizator; nimic altceva despre tine nu le este arătat. Temei: contractul, pentru o funcție pe care alegi să o folosești (art. 6 alin. (1) lit. b) GDPR). Păstrare: până elimini prietenul sau îți ștergi contul; cererile fără răspuns se șterg după 90 de zile, iar linkurile de invitație nu mai merg după 7 zile.",
  LEGAL_P2_L7:
    "Bonus Resto. Dacă strângi ștampile: fiecare ștampilă (localul și ziua), bonusul tău, când și unde l-ai folosit și încercările de PIN greșite (ca să oprim ghicirea). Când apeși Sunt aici! sau folosești un bonus, poziția ta și precizia ei sunt verificate o singură dată pe serverul nostru și nu se salvează. Localurile văd doar totaluri și o listă cu momentul și produsul fiecărui bonus folosit, niciodată cine. Temei: contractul, conform Regulamentului Bonus (art. 6 alin. (1) lit. b) GDPR). Cât le păstrăm: vezi mai jos.",
  LEGAL_P2_L8:
    "Raportări. Dacă trimiți o raportare: ce ai scris, despre ce e (aplicația sau un local și pagina), o captură opțională, contul tău și momentul. O vede doar echipa Resto. Temei: obligația noastră legală de a trata notificările (Regulamentul UE privind serviciile digitale, art. 16; art. 6 alin. (1) lit. c) GDPR) și interesul nostru legitim de a repara problemele (art. 6 alin. (1) lit. f)). Păstrare: până o rezolvăm, apoi 180 de zile.",
  LEGAL_P2_L9:
    "Cereri de parteneriat. Dacă trimiți formularul Deveniți partener pentru un restaurant: numele și rolul tău, emailul, telefonul, restaurantul, orașul, sistemul POS și mărimea lui și mesajul tău. Verificăm că domeniul emailului poate primi mesaje și păstrăm un hash ireversibil al adresei IP, ca să oprim trimiterile în masă. Temei: demersurile cerute de tine înainte de un contract (art. 6 alin. (1) lit. b) GDPR) și interesul nostru legitim de a-ți răspunde (art. 6 alin. (1) lit. f)). Păstrare: cât timp discutăm; cererile fără urmare și spamul se șterg după 12 luni.",
  LEGAL_P3_H: "Locația",
  LEGAL_P3_P:
    "Resto îți folosește locația doar cât timp e deschis pe ecran și doar după ce o permiți în browser. Ordonează localurile după distanță și afișează butonul Sunt aici! la cel mult 75 m de un local. La check-in sau când folosești un bonus, poziția ta e trimisă o singură dată, ca serverul să confirme distanța; nu o salvăm, păstrăm doar localul și ziua. Vizitele automate (o vizită numărată după 10 minute într-un local pe care l-ai deschis) rămân oprite până le pornești din Setări. Resto nu îți urmărește niciodată locația în fundal.",
  LEGAL_P4_H: "Pe dispozitivul tău",
  LEGAL_P4_P:
    "Resto păstrează pe dispozitivul tău: limba și tema, alegerea privind consimțământul, ID-ul anonim dacă l-ai permis, o listă scurtă cu localurile deschise (pentru ordonarea feedului), dacă vizitele automate sunt pornite, când ți-am cerut ultima dată o notă, o invitație de prietenie deschisă înainte de autentificare (cel mult o oră), dacă ți-am sugerat deja un cont în această vizită și, când ești autentificat, sesiunea. Fără cookie-uri de reclamă sau de urmărire de la terți.",
  LEGAL_P5_H: "Feedback și AI",
  LEGAL_P5_P1:
    "Feedbackul tău e privat. Localurile văd doar numere și medii (vizite, nota medie, suma medie), niciodată numele sau emailul tău. Echipa Resto vede aceleași cifre plus scurte rezumate AI ale temelor recurente și accesează înregistrările doar ca să opereze și să protejeze serviciul. Rezumatele sunt scrise de un model AI din notele recente ale unui local, așa că textul notelor e trimis furnizorului nostru de AI doar în acest scop. Când folosești Întreabă Resto, ce scrii e trimis furnizorului de AI ca să devină filtre. Unele descrieri de localuri și texte de oferte sunt scrise cu ajutorul AI și verificate de echipă înainte de publicare.",
  LEGAL_P5_P2:
    "Până când activăm un furnizor de AI, aceste funcții merg pe reguli proprii și nu se trimite nimic nicăieri; după ce îl activăm, îl numim aici.",
  LEGAL_P6_H: "Cine ne ajută",
  LEGAL_P6_L1: "Supabase: baza de date, conturile și emailurile de cont, găzduite la Frankfurt (UE).",
  LEGAL_P6_L2: "Fly.io: serverul nostru API, în regiunea Frankfurt (UE).",
  LEGAL_P6_L3: "Netlify: găzduiește site-ul și îl livrează în toată lumea.",
  LEGAL_P6_L4: "Google: doar dacă te autentifici cu Google.",
  LEGAL_P6_L5: "Cloudflare: captcha-ul Turnstile de pe formularele de autentificare și înregistrare și de pe formularul de parteneriat.",
  LEGAL_P6_L6: "CARTO: hărțile pe care browserul tău le încarcă atunci când deschizi harta (CARTO vede adresa ta IP).",
  LEGAL_P6_L7: "Furnizorul nostru de AI: doar pentru funcțiile AI de mai sus, după ce e activat.",
  LEGAL_P6_P:
    "Unii dintre ei sunt companii din SUA sau pot prelucra date în afara UE. Aceste transferuri se bazează pe Cadrul UE–SUA privind protecția datelor (Data Privacy Framework) sau pe clauzele contractuale standard ale Comisiei Europene. Nu vindem datele tale și nu le folosim pentru reclame.",
  LEGAL_P7_H: "Cât timp păstrăm datele",
  LEGAL_P7_L1: "Evenimente de utilizare: 180 de zile.",
  LEGAL_P7_L2:
    "Vizite și feedback: 24 de luni, apoi doar totaluri lunare pe local, fără legătură cu tine.",
  LEGAL_P7_L3: "Datele contului: până îți ștergi contul.",
  LEGAL_P7_L4:
    "Datele de vizitator legate de un ID anonim: până uiți dispozitivul, în limitele de mai sus.",
  LEGAL_P7_L5:
    "Prieteni: până elimini prietenul sau îți ștergi contul; cererile fără răspuns, 90 de zile; linkurile de invitație, 7 zile.",
  LEGAL_P7_L6:
    "Bonus Resto: ștampilele, 13 luni (fiecare e valabilă 12); bonusurile și evidența bonusurilor folosite, 24 de luni; un ecran de bonus neconfirmat, o zi; încercările de PIN greșite, 30 de zile.",
  LEGAL_P7_L7: "Raportări: 180 de zile după ce le rezolvăm.",
  LEGAL_P7_L8: "Cereri de parteneriat: cât timp discutăm; cele fără urmare și spamul, 12 luni.",
  LEGAL_P7_P: "Aceste limite se aplică automat în fiecare noapte.",
  LEGAL_P8_H: "Drepturile tale",
  LEGAL_P8_P1:
    "Ai dreptul de acces la date, la rectificarea sau ștergerea lor, la restricționarea prelucrării, la portabilitate și dreptul de a te opune prelucrării bazate pe interesul nostru legitim. Acolo unde ne bazăm pe consimțământul tău, îl poți retrage oricând, fără să afecteze prelucrarea de dinainte.",
  LEGAL_P8_P2:
    "În aplicație: îți ștergi contul din meniul contului (se șterg și vizitele, feedbackul, evenimentele, datele despre prieteni și cele despre bonus; un bonus folosit rămâne în evidența localului, fără nicio legătură cu tine) sau uiți dispozitivul din Setări. Pentru orice altceva, scrie-ne la {email}; răspundem în cel mult o lună. Poți depune și o plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP, dataprotection.ro).",
  LEGAL_P9_H: "Vârsta",
  LEGAL_P9_P:
    "Conturile Resto sunt pentru persoane de cel puțin 16 ani. Dacă crezi că cineva mai tânăr și-a făcut cont, scrie-ne și îl ștergem.",
  LEGAL_P10_H: "Modificări",
  LEGAL_P10_P: "Când se schimbă ce facem cu datele, actualizăm această politică și data de sus.",
  LEGAL_BONUS_TITLE: "Regulament Bonus Resto",
  LEGAL_BONUS_INTRO:
    "Versiunea 1.0, octombrie 2026. Acest regulament se aplică programului Bonus Resto, un card de ștampile organizat de {controllers}. Programul funcționează doar în localurile care aleg să participe; aplicația le afișează.",
  LEGAL_B1_H: "Cine poate participa",
  LEGAL_B1_P:
    "Oricine are cel puțin 16 ani și un cont Resto. Conturile de personal (managerii localurilor și echipa Resto) nu pot participa. Ștampilele încep să se numere la {minAccountAgeHours} de ore după crearea contului. Strângerea ștampilelor nu costă nimic.",
  LEGAL_B2_H: "Cum strângi ștampile",
  LEGAL_B2_L1:
    "Primești o ștampilă când apeși „Sunt aici!” într-un local participant, cât ești acolo: locația telefonului trebuie să fie la cel mult 75 m de local, cu o precizie de 50 m sau mai bună. Locația e folosită doar pentru această verificare și nu e stocată.",
  LEGAL_B2_L2: "Se numără cel mult 1 ștampilă pe zi (ora României), în toate localurile la un loc.",
  LEGAL_B2_L3: "Vizitele numărate automat (setarea „Numără-mi vizitele automat”) nu aduc niciodată ștampile.",
  LEGAL_B2_L4: "O ștampilă e valabilă 12 luni.",
  LEGAL_B3_H: "Bonusul",
  LEGAL_B3_L1:
    "{stampsRequired} ștampile îți aduc un bonus: un produs stabilit de localul în care îl folosești, valabil la orice comandă. Produsul este fără alcool și costă cel mult {maxItemRon} lei în meniu. Un local poate cere o comandă minimă; aceasta e afișată lângă produs.",
  LEGAL_B3_L2: "Poți avea un singur bonus odată. Cât îl ai, poți strânge până la {stampsWhileHolding} ștampile pentru următorul.",
  LEGAL_B3_L3:
    "Un bonus e valabil 90 de zile de când îl primești. Nu poate fi schimbat pe bani, dat altcuiva sau cumulat cu alt bonus.",
  LEGAL_B4_H: "Cum folosești bonusul",
  LEGAL_B4_L1:
    "Îl poți folosi în orice local participant. În local (la cel mult 75 m, cu aceeași verificare a locației ca la ștampile), apasă „Folosește bonusul” și arată ecranul personalului: apar localul, produsul și o numărătoare inversă de 3 minute. Un local confirmă cel mult {maxPerVenueDay} de bonusuri pe zi; după aceea, îl poți folosi acolo a doua zi sau în alt local participant.",
  LEGAL_B4_L2:
    "Un membru al personalului introduce PIN-ul localului pe telefonul tău, ca să confirme. După {pinMaxTries} PIN-uri greșite, folosirea bonusului în acel local e oprită {pinLockMinutes} minute; după {pinMaxFailsPerDay} PIN-uri greșite într-o zi, în orice localuri, e oprită până a doua zi.",
  LEGAL_B4_L3: "După confirmare, bonusul e folosit și nu mai poate fi folosit din nou.",
  LEGAL_B5_H: "Când un local își schimbă produsul sau iese din program",
  LEGAL_B5_P:
    "Un local își poate schimba produsul-bonus cu un preaviz de 7 zile, afișat în aplicație; primești produsul afișat în momentul în care începi să folosești bonusul. Un local care iese din program onorează timp de 30 de zile bonusurile câștigate înainte de ieșire.",
  LEGAL_B6_H: "Joc corect",
  LEGAL_B6_P:
    "Ștampilele și bonusurile sunt personale. Putem anula ștampilele sau bonusurile obținute prin falsificarea locației, prin folosirea mai multor conturi pe același dispozitiv sau prin orice alt abuz și putem suspenda ștampilele unui cont cât verificăm. Un local poate refuza un bonus fără comandă.",
  LEGAL_B7_H: "Fără element de noroc",
  LEGAL_B7_P: "Bonusul depinde doar de ștampilele pe care le strângi. Nu există tragere la sorți, loterie sau alt element de noroc.",
  LEGAL_B8_H: "Datele tale",
  LEGAL_B8_P:
    "Ștampilele, bonusul și folosirile lui sunt păstrate în contul tău. Localurile văd doar totaluri și o listă cu momentele în care s-a folosit un bonus și pentru ce produs, niciodată cine l-a folosit.",
  LEGAL_B9_H: "Modificarea regulamentului",
  LEGAL_B9_P:
    "Publicăm fiecare versiune nouă pe această pagină, cu numărul și data ei, înainte să se aplice. O modificare nu anulează niciodată ștampilele sau bonusul pe care le ai deja.",
  LEGAL_B10_H: "Contact",
  LEGAL_B10_P: "Întrebări sau reclamații despre Bonus Resto: {email}.",

  // --- It-6 privacy controls, 16+, Report link, AI label -------------------------
  SIDEBAR_AUTO_VISITS: "Numără-mi vizitele automat",
  SIDEBAR_AUTO_VISITS_HINT:
    "Numără o vizită după 10 minute într-un local pe care l-ai deschis, cât timp Resto e pe ecran. Reține doar localul și ziua.",
  SIDEBAR_DEVICE_ID: "ID anonim de dispozitiv",
  SIDEBAR_DEVICE_ID_ON: "Pornit: îți numără vizitele fără cont.",
  SIDEBAR_DEVICE_ID_OFF: "Oprit.",
  SIDEBAR_DEVICE_ALLOW: "Permite",
  SIDEBAR_DEVICE_FORGET: "Uită acest dispozitiv",
  SIDEBAR_DEVICE_FORGET_CONFIRM: "Apasă din nou ca să ștergi",
  SIDEBAR_DEVICE_FORGOTTEN: "Gata. Datele de vizitator de pe acest dispozitiv au fost șterse.",
  SIDEBAR_DEVICE_FORGET_ERROR: "Nu am putut șterge acum, deci nu s-a schimbat nimic. Încearcă mai târziu.",
  AUTH_AGE_16: "Am cel puțin 16 ani și accept",
  AUTH_AGE_TERMS: "Termenii",
  AUTH_GOOGLE_AGE_NOTE:
    "Prima dată pe Resto? Dacă îți faci cont cu Google, confirmi că ai cel puțin 16 ani și accepți Termenii.",
  AUTH_SOCIAL_AGE_NOTE:
    "Prima dată pe Resto? Dacă îți faci cont cu Google sau Apple, confirmi că ai cel puțin 16 ani și accepți Termenii.",
  REPORT_LINK: "Raportează",
  REPORT_ARIA: "Raportează {what}",
  REPORT_SUBJECT: "Raportare: {what}",
  REPORT_BODY:
    "Ce și unde: {what} ({url})\nDe ce e o problemă:\n\nNumele tău:\nEmailul tău:\n\nConfirm că raportez cu bună-credință și că informațiile de mai sus sunt corecte.",
  AI_ASK_SUGGESTED: "Filtre sugerate de AI",

  // --- Venue pages, 404, page titles (Oct 28; venue-detail-offers.md, seo-geo.md) ---
  DETAIL_HOURS_DAILY: "Zilnic {hours}",
  DETAIL_SHARE: "Distribuie",
  DETAIL_LINK_COPIED: "Link copiat",
  NOT_FOUND_TITLE: "Pagina asta nu e în meniu",
  NOT_FOUND_BODY: "Linkul poate fi vechi sau localul nu mai e listat. Continuă de aici.",
  NOT_FOUND_CITY: "Restaurante în Cluj-Napoca",
  NOT_FOUND_HOME: "Alege un oraș",
  META_NOT_FOUND_TITLE: "Pagină negăsită | Resto",
  META_WELCOME_TITLE: "Resto: vezi unde e loc în seara asta",
  META_WELCOME_DESC: "Resto îți arată ce restaurante, cafenele și baruri sunt deschise acum, ofertele lor cât timp rulează și, cu un cont gratuit, cât de pline sunt, citit live din POS-ul localului.",
  META_CITY_TITLE: "Restaurante în {city}: deschise acum și capacitate live | Resto",
  META_CITY_DESC: "Vezi ce restaurante, cafenele și baruri din {city} sunt deschise acum, ce oferte au și, cu un cont gratuit, cât de pline sunt.",
  META_VENUE_TITLE: "{name}, {city} | Resto",
  META_VENUE_DESC: "{name} din {city}: program, poze, oferte și disponibilitate live pe Resto.",
  META_LEGAL_SUFFIX: "{title} | Resto",
  META_LANDING_TITLE: "Resto: disponibilitate live în restaurante, pentru clienți și localuri",
  META_LANDING_DESC: "Cum funcționează Resto: ce e deschis acum, oferte și capacitate live citită din POS-ul localului pentru clienți, și un preț lunar fix pentru restaurante.",
  META_MANAGE_TITLE: "Panoul localului | Resto",
  // --- Reports (§35b; docs/features/reports.md) ------------------------------------
  SIDEBAR_REPORT_ENTRY: "Raportează o problemă",
  REPORT_TITLE: "Raportează o problemă",
  REPORT_KIND_APP: "Aplicația",
  REPORT_KIND_VENUE: "Un local",
  REPORT_ABOUT: "Despre: {what}",
  REPORT_VENUE_HOWTO: "Ca să raportezi un local, deschide-i pagina și apasă Raportează, jos. Ajungi aici cu localul deja completat.",
  REPORT_BODY_LABEL: "Ce nu e în regulă?",
  REPORT_BODY_HINT_APP: "Ce ai făcut, ce te așteptai, ce s-a întâmplat.",
  REPORT_BODY_HINT_VENUE: "Informații greșite, un local închis definitiv, o ofertă care nu există…",
  REPORT_BODY_TOO_SHORT: "Încă câteva cuvinte, te rog (minimum 10 caractere).",
  REPORT_SCREENSHOT: "Captură de ecran (opțional)",
  REPORT_SCREENSHOT_ADD: "Adaugă o captură",
  REPORT_SCREENSHOT_REMOVE: "Scoate",
  REPORT_SCREENSHOT_ONLY: "O imagine (PNG, JPG sau WebP). Fără video.",
  REPORT_SCREENSHOT_BAD: "Fișierul nu e o imagine pe care o putem folosi. Alege o captură (PNG, JPG sau WebP).",
  REPORT_GOOD_FAITH: "Raportez cu bună-credință și ce am scris e corect.",
  REPORT_SEND: "Trimite raportul",
  REPORT_SENDING: "Se trimite…",
  REPORT_SENT_TITLE: "Mulțumim, l-am primit",
  REPORT_SENT_BODY: "Cineva din echipă citește fiecare raport. Dacă avem nevoie de mai multe, îți scriem pe emailul contului.",
  REPORT_DONE: "Gata",
  REPORT_ERR_RATE: "Poți trimite un raport pe zi. Următorul: {time}.",
  REPORT_ERR_UNCONFIRMED: "Confirmă-ți mai întâi emailul, apoi poți trimite un raport.",
  REPORT_ERR_GENERIC: "Nu s-a putut trimite. Încearcă din nou peste puțin timp.",
  REPORT_ERR_UPLOAD: "Captura nu s-a încărcat. Încearcă din nou sau trimite fără ea.",
  REPORT_GUEST_TITLE: "Intră în cont ca să raportezi",
  REPORT_GUEST_BODY: "Rapoartele vin din conturi, ca să putem reveni și să ținem spamul departe. N-ai cont? Scrie-ne pe email.",
  REPORT_GUEST_SIGNIN: "Intră în cont",

  // --- Friends (It-8, §32; docs/features/friends.md) ----------------------------
  FRIENDS_TITLE: "Prieteni",
  FRIENDS_ENTRY: "Prietenii tăi",
  FRIENDS_ENTRY_PICK: "Alege un nume de utilizator ca să începi",
  FRIENDS_REQUESTS_BADGE: "{n} cereri de prietenie în așteptare",
  FRIENDS_LOADING: "Se încarcă prietenii",
  FRIENDS_ERROR: "Ceva nu a mers. Încearcă din nou.",
  FRIENDS_RETRY: "Încearcă din nou",
  FRIENDS_PRIVACY: "Prietenii văd doar numele tău și @numele de utilizator. Nimic altceva nu se partajează.",
  FRIENDS_USERNAME_INTRO: "Alege un nume de utilizator ca prietenii să te poată adăuga. Îl poți schimba oricând.",
  FRIENDS_USERNAME_LABEL: "Nume de utilizator",
  FRIENDS_USERNAME_HINT: "Între 3 și 20 de caractere: a-z, 0-9 și _",
  FRIENDS_USERNAME_SAVE: "Salvează numele",
  FRIENDS_USERNAME_TAKEN: "Numele acesta e deja folosit. Încearcă altul.",
  FRIENDS_USERNAME_RESERVED: "Numele acesta e rezervat. Încearcă altul.",
  FRIENDS_USERNAME_INVALID: "Folosește între 3 și 20 de caractere: a-z, 0-9 și _.",
  FRIENDS_USERNAME_CHANGE: "Schimbă",
  FRIENDS_CANCEL: "Anulează",
  FRIENDS_INVITE_TITLE: "Invită un prieten",
  FRIENDS_INVITE_BODY: "Oricine îți deschide linkul te poate adăuga direct. Linkul merge 7 zile.",
  FRIENDS_INVITE_COPY: "Copiază linkul",
  FRIENDS_INVITE_COPIED: "Copiat",
  FRIENDS_INVITE_SHARE: "Trimite",
  FRIENDS_INVITE_SHARE_TEXT: "Adaugă-mă ca prieten pe Resto",
  FRIENDS_INVITE_QR: "Cod QR",
  FRIENDS_INVITE_VALID_UNTIL: "Link valabil până pe {d}",
  FRIENDS_INVITE_RESET: "Link nou",
  FRIENDS_INVITE_RESET_CONFIRM: "Linkul vechi nu va mai merge. Apasă din nou ca să confirmi",
  FRIENDS_QR_TITLE: "Scanează ca să adaugi @{u}",
  FRIENDS_QR_CLOSE: "Închide",
  FRIENDS_ADD_TITLE: "Adaugă după numele de utilizator",
  FRIENDS_ADD_PLACEHOLDER: "nume_utilizator",
  FRIENDS_ADD_SEND: "Trimite cererea",
  FRIENDS_ADD_SENT: "Cerere trimisă.",
  FRIENDS_ADD_FRIENDS: "Ți-a trimis deja o cerere. Acum sunteți prieteni.",
  FRIENDS_ADD_ALREADY_FRIENDS: "Sunteți deja prieteni.",
  FRIENDS_ADD_ALREADY_SENT: "I-ai trimis deja o cerere.",
  FRIENDS_ADD_NOT_FOUND: "Nimeni nu are numele acesta. Verifică scrierea.",
  FRIENDS_ADD_SELF: "Acesta e chiar numele tău.",
  FRIENDS_ADD_BLOCKED: "Ai blocat această persoană. Deblocheaz-o mai întâi.",
  FRIENDS_RATE_LIMITED: "Ai atins limita de azi. Încearcă mâine.",
  FRIENDS_REQUESTS_TITLE: "Cereri",
  FRIENDS_ACCEPT: "Acceptă",
  FRIENDS_DECLINE: "Refuză",
  FRIENDS_LIST_TITLE: "Prieteni",
  FRIENDS_EMPTY: "Încă nu ai prieteni. Trimite-ți linkul sau adaugă pe cineva după numele de utilizator.",
  FRIENDS_SENT_TITLE: "Trimise",
  FRIENDS_MORE: "Mai multe opțiuni pentru {name}",
  FRIENDS_REMOVE: "Elimină",
  FRIENDS_REMOVE_CONFIRM: "Apasă din nou ca să elimini",
  FRIENDS_BLOCK: "Blochează",
  FRIENDS_BLOCK_CONFIRM: "Apasă din nou ca să blochezi",
  FRIENDS_BLOCKED_TITLE: "Blocați ({n})",
  FRIENDS_UNBLOCK: "Deblochează",
  FRIEND_INVITE_TITLE: "{name} te-a invitat să fiți prieteni pe Resto",
  FRIEND_INVITE_SUB: "Prietenii văd unul despre celălalt doar numele și @numele de utilizator.",
  FRIEND_INVITE_SIGNED_OUT_SUB: "Ai nevoie de un cont gratuit ca să adaugi prieteni. Revii aici după aceea.",
  FRIEND_INVITE_SIGNUP: "Creează un cont gratuit",
  FRIEND_INVITE_SIGNIN: "Am deja cont",
  FRIEND_INVITE_ADD: "Adaugă @{u}",
  FRIEND_INVITE_PICK_USERNAME: "Mai întâi, alege-ți numele de utilizator.",
  FRIEND_INVITE_SUCCESS: "Tu și {name} sunteți acum prieteni",
  FRIEND_INVITE_ALREADY: "Tu și {name} sunteți deja prieteni",
  FRIEND_INVITE_SELF: "Acesta e propriul tău link de invitație",
  FRIEND_INVITE_SELF_SUB: "Trimite-l unui prieten ca să te poată adăuga.",
  FRIEND_INVITE_INVALID_TITLE: "Invitația nu mai merge",
  FRIEND_INVITE_INVALID_SUB: "Poate a expirat sau a fost înlocuită. Cere-i prietenului tău un link nou.",
  FRIEND_INVITE_STAFF: "Conturile de personal nu pot adăuga prieteni. Folosește un cont personal.",
  FRIEND_INVITE_OPEN_FRIENDS: "Deschide Prieteni",
  FRIEND_INVITE_OPEN_APP: "Deschide aplicația",

  // --- Bonus (It-10, §33; docs/features/bonus.md) -------------------------------
  BONUS_TITLE: "Bonus",
  BONUS_ENTRY: "Card de ștampile",
  BONUS_ENTRY_PROGRESS: "{n} din {total} ștampile",
  BONUS_ENTRY_READY: "Bonus gata de folosit",
  BONUS_CARD_ARIA: "{n} din {total} ștampile strânse",
  BONUS_HOW:
    "Apasă Sunt aici! într-un local participant ca să primești o ștampilă, cel mult {perDay} pe zi. {total} ștampile îți aduc un bonus.",
  BONUS_STAMPED_TODAY: "Ai primit o ștampilă azi.",
  BONUS_STAMP_TTL: "Fiecare ștampilă e valabilă {days} de zile.",
  BONUS_CONDITION: "Bonus: {item}, valabil la orice comandă",
  BONUS_MIN_ORDER: "Comandă minimă {ron} lei",
  BONUS_HELD_TITLE: "Ai un bonus",
  BONUS_HELD_BODY:
    "Folosește-l până pe {date}, în orice local participant, la o comandă. Produsul e cel oferit de localul respectiv.",
  BONUS_HELD_CAP: "Cât timp îl ai, cardul se umple până la {n} ștampile.",
  BONUS_VENUES_TITLE: "Localuri participante",
  BONUS_VENUES_EMPTY: "Niciun local nu participă încă. Apar aici pe măsură ce se alătură.",
  BONUS_HONOURS_UNTIL: "A ieșit din program; acceptă bonusuri până pe {date}",
  BONUS_NEXT_ITEM: "De pe {date}: {item}",
  BONUS_USE_HERE: "Folosește bonusul aici",
  BONUS_DISTANCE: "La {distance}",
  BONUS_ENABLE_LOCATION: "Activează locația ca să-l folosești",
  BONUS_LAST_USED: "Ultimul bonus folosit {time} la {venue}",
  BONUS_RULES_LINK: "Regulament Bonus",
  BONUS_LOADING: "Se încarcă cardul de ștampile",
  BONUS_ERROR: "Nu am putut încărca cardul de ștampile.",
  BONUS_START_TOO_FAR: "Trebuie să fii în local, la cel mult {m} m.",
  BONUS_START_LOW_ACCURACY:
    "Locația ta nu e încă destul de precisă (e nevoie de {m} m sau mai bine). Încearcă din nou peste câteva clipe.",
  BONUS_START_LOCATION: "Activează locația și încearcă din nou.",
  BONUS_START_VENUE_LIMIT: "Localul a atins limita de bonusuri pe azi. Încearcă alt local sau altă zi.",
  BONUS_START_NOT_PARTICIPATING: "Localul nu acceptă bonusuri acum.",
  BONUS_START_NO_BONUS: "Nu ai un bonus acum.",
  BONUS_START_ALREADY_USED: "Bonusul acesta e deja folosit.",
  BONUS_ERROR_GENERIC: "Ceva n-a mers. Încearcă din nou.",
  BONUS_REDEEM_TITLE: "Folosește bonusul",
  BONUS_REDEEM_SHOW: "Arată ecranul personalului",
  BONUS_REDEEM_EXPIRES: "Expiră în {time}",
  BONUS_REDEEM_LIVE: "Live",
  BONUS_REDEEM_STAFF: "Personal: introduceți PIN-ul localului pentru confirmare",
  BONUS_PIN_LABEL: "PIN-ul localului",
  BONUS_PIN_DELETE: "Șterge o cifră",
  BONUS_PIN_CONFIRM: "Confirmă",
  BONUS_PIN_BAD: "PIN greșit. Încercări rămase: {n}.",
  BONUS_PIN_LOCKED: "Prea multe PIN-uri greșite. Încearcă din nou după {time}.",
  BONUS_PIN_UNSET: "Localul nu a setat încă un PIN.",
  BONUS_REDEEM_EXPIRED: "Ecranul a expirat.",
  BONUS_REDEEM_RESTART: "Pornește din nou",
  BONUS_REDEEM_CANCEL: "Nu acum",
  BONUS_USED_TITLE: "Folosit ✓",
  BONUS_USED_AT: "{time} la {venue}",
  BONUS_USED_DONE: "Gata",
  BONUS_CHIP_TITLE: "Local Bonus Resto",
  BONUS_CHIP_HINT: "Primești o ștampilă aici cu Sunt aici!",
  BONUS_CHIP_HONOURING: "Acceptă bonusuri până pe {date}; aici nu se mai strâng ștampile.",
  // Manager Bonus tab (/manage; the operator editor reuses the EN strings).
  MGR_TAB_BONUS: "Bonus",
  MGR_BONUS_TITLE: "Bonus Resto",
  MGR_BONUS_DESC:
    "Opțional. Clienții primesc o ștampilă când apasă Sunt aici! într-un local participant; un card plin le aduce un produs bonus, valabil la orice comandă, în orice local participant.",
  MGR_BONUS_RULES_LINK: "Citește Regulamentul Bonus",
  MGR_BONUS_UNAVAILABLE: "Bonusul nu e disponibil încă.",
  MGR_BONUS_STATUS_ON: "Participi",
  MGR_BONUS_STATUS_OFF: "Nu participi",
  MGR_BONUS_ENABLE: "Participă la Bonus Resto",
  MGR_BONUS_ENABLE_HINT:
    "Setează întâi produsul și PIN-ul. Dacă ieși, accepți în continuare, timp de 30 de zile, bonusurile câștigate înainte să ieși.",
  MGR_BONUS_HONOURING: "Ai ieșit din program. Accepți bonusurile anterioare până pe {date}.",
  MGR_BONUS_NEEDS_ITEM: "Salvează întâi produsul bonus.",
  MGR_BONUS_NEEDS_PIN: "Setează întâi PIN-ul pentru personal.",
  MGR_BONUS_ITEM_TITLE: "Produsul bonus",
  MGR_BONUS_ITEM_HINT:
    "Un produs standard, ieftin, fără alcool. Clienții văd „Bonus: [produs], valabil la orice comandă”. Cât timp participi, o schimbare se aplică la 7 zile după ce o salvezi.",
  MGR_BONUS_ITEM_NAME: "Denumirea produsului",
  MGR_BONUS_ITEM_NAME_RO: "Denumirea în română (dacă diferă)",
  MGR_BONUS_ITEM_VALUE: "Preț în meniu, lei (cel mult {cap})",
  MGR_BONUS_MIN_ORDER: "Comandă minimă, lei (opțional)",
  MGR_BONUS_NON_ALCOHOLIC: "Confirm că produsul nu conține alcool.",
  MGR_BONUS_SAVE_ITEM: "Salvează produsul",
  MGR_BONUS_ITEM_SAVED: "Produs salvat.",
  MGR_BONUS_ITEM_SCHEDULED: "Salvat. Clienții sunt anunțați cu 7 zile înainte: se aplică pe {date}.",
  MGR_BONUS_NEXT: "Programat pentru {date}: {item}",
  MGR_BONUS_ERR_ITEM: "Denumirea trebuie să aibă 2-60 de caractere.",
  MGR_BONUS_ERR_VALUE: "Introdu prețul din meniu ca număr de lei.",
  MGR_BONUS_ERR_OVER_CAP: "Prețul poate fi cel mult {cap} lei.",
  MGR_BONUS_ERR_MIN_ORDER: "Introdu comanda minimă ca număr de lei (sau las-o goală).",
  MGR_BONUS_ERR_NON_ALCOHOLIC: "Confirmă că produsul e fără alcool.",
  MGR_BONUS_PIN_TITLE: "PIN pentru personal",
  MGR_BONUS_PIN_HINT:
    "Personalul îl introduce pe telefonul clientului ca să confirme bonusul: 4-8 cifre. Un PIN nou se aplică imediat. Spune-l doar echipei tale.",
  MGR_BONUS_PIN_NEW: "PIN nou",
  MGR_BONUS_PIN_REPEAT: "Repetă PIN-ul",
  MGR_BONUS_PIN_SAVE: "Salvează PIN-ul",
  MGR_BONUS_PIN_SET_AT: "PIN setat pe {date}",
  MGR_BONUS_PIN_NONE: "Încă niciun PIN",
  MGR_BONUS_PIN_SAVED: "PIN salvat.",
  MGR_BONUS_ERR_PIN: "PIN-ul trebuie să aibă 4-8 cifre.",
  MGR_BONUS_ERR_PIN_MATCH: "Cele două PIN-uri nu se potrivesc.",
  MGR_BONUS_STATS_TITLE: "Ultimele 30 de zile",
  MGR_BONUS_STAT_STAMPS: "Ștampile date aici",
  MGR_BONUS_STAT_USED: "Bonusuri folosite aici",
  MGR_BONUS_LOG_TITLE: "Bonusuri folosite aici",
  MGR_BONUS_LOG_HINT: "Doar ora și produsul. Resto nu arată niciodată cine a folosit bonusul.",
  MGR_BONUS_LOG_EMPTY: "Niciunul încă.",
  MGR_BONUS_ERR_GENERIC: "Nu am putut salva. Încearcă din nou.",

  // --- Manager editor (/manage) — the one bilingual staff surface --------------
  MGR_EYEBROW: "Resto · Manager de local",
  MGR_FALLBACK_VENUE: "Localul dvs.",
  MGR_SUBTITLE: "Pagina localului, ofertele și evenimentele dvs. Ce modificați aici, clienții văd imediat.",
  MGR_SIGNOUT: "Ieșiți din cont",
  MGR_TABS_ARIA: "Secțiuni manager",
  MGR_TAB_EDITOR: "Editare",
  MGR_TAB_CONVERSIONS: "Conversii",
  MGR_LOADING: "Se încarcă…",
  MGR_ERR_NO_VENUE: "Contul dvs. nu e legat încă de niciun local. Scrieți-ne și rezolvăm.",
  MGR_ERR_NOT_FOUND: "Localul nu a fost găsit.",
  MGR_ERR_LOAD: "Nu s-a putut încărca",
  MGR_ERR_NAME_REQUIRED: "Numele e obligatoriu.",
  MGR_ERR_TIME_OPEN: "Ora de deschidere trebuie să fie HH:MM.",
  MGR_ERR_TIME_CLOSE: "Ora de închidere trebuie să fie HH:MM.",
  MGR_ERR_SAVE: "Salvarea nu a reușit",
  MGR_SAVED: "Salvat.",
  MGR_UNSAVED: "Modificări nesalvate",
  MGR_SAVE_CTA: "Salvați modificările",
  MGR_PHOTOS_TITLE: "Fotografii",
  MGR_PHOTOS_DESC: "Imaginea principală și până la patru fotografii în galerie.",
  MGR_PHOTOS_HERO: "Imagine principală",
  MGR_PHOTOS_GALLERY: "Galerie (maximum 4)",
  MGR_IDENTITY_TITLE: "Identitate",
  MGR_IDENTITY_DESC: "Numele și descrierile pe care le văd clienții. Completați câmpurile (RO) pentru clienții români; dacă rămân goale, se afișează textul în engleză.",
  MGR_FIELD_NAME: "Nume *",
  MGR_FIELD_NEIGHBORHOOD: "Cartier",
  MGR_PH_NEIGHBORHOOD: "Centrul Vechi",
  MGR_FIELD_MAPS: "Link Google Maps",
  MGR_CUISINE_LABEL: "Bucătărie (alegeți una sau mai multe)",
  MGR_TAGS_LABEL: "Etichete: atmosferă și dotări",
  MGR_DESC_EN: "Descriere (EN)",
  MGR_DESC_RO: "Descriere (RO)",
  MGR_DESC_EN_PH: "What makes this place special: the room, the menu, the vibe.",
  MGR_DESC_RO_PH: "Ce face acest loc special: sala, meniul, atmosfera.",
  MGR_HOURS_TITLE: "Program",
  MGR_HOURS_DESC: "Folosit de aplicație pentru a afișa deschis / închis.",
  MGR_OPENS_AT: "Se deschide la",
  MGR_CLOSES_AT: "Se închide la",
  MGR_HOURS_HINT: "Programul poate trece de miezul nopții (ex. deschide la 18:00, închide la 02:00).",
  MGR_CAPACITY_TITLE: "Afișarea capacității",
  MGR_CAPACITY_DESC: "Cum văd clienții capacitatea dvs. în timp real (activă pentru localul dvs.).",
  MGR_CAP_EXACT: "Arată locurile exacte",
  MGR_CAP_DISCREET: "Discret (fără cifre)",
  MGR_CAP_EXACT_HINT: "Clienții văd exact câte locuri sunt libere, plus o bară de ocupare.",
  MGR_CAP_DISCREET_HINT: "Clienții văd nivelul de ocupare și o bară aproximativă, niciodată o cifră sau o sală care pare goală.",
  MGR_RES_TITLE: "Rezervări",
  MGR_RES_DESC: "Spuneți-le clienților cât de rezervat sunteți azi. Apare ca reper pe cardul dvs. Resto nu este un sistem de rezervări.",
  MGR_RES_ENABLED: "Acceptă rezervări",
  MGR_RES_ENABLED_HINT: "Afișează pe cardul localului semnul „acceptă rezervări”.",
  MGR_RES_STATUS_LABEL: "Statusul de azi",
  MGR_RES_ST_AVAILABLE: "Disponibil",
  MGR_RES_ST_LIMITED: "Limitat",
  MGR_RES_ST_FULL: "Complet rezervat",
  MGR_RES_STATUS_HINT: "Doar orientativ: mâine revine la semnul neutru. Reselectați valoarea de azi ca să o reîmprospătați.",
  MGR_POS_TITLE: "Conexiunea POS",
  MGR_POS_DESC: "Feedul de ocupare live al localului dvs. Noi configurăm și administrăm conexiunea; aici nu aveți nimic de făcut.",
  MGR_POS_STATUS_PENDING: "În configurare",
  MGR_POS_STATUS_ACTIVE: "Activă",
  MGR_POS_STATUS_ERROR: "Problemă de sincronizare",
  MGR_POS_LAST_SYNC: "Ultima sincronizare: {time}",
  MGR_POS_NEVER: "Încă nesincronizată",
  MGR_POS_HINT_PENDING: "Conexiunea e configurată și așteaptă prima sincronizare reușită.",
  MGR_POS_HINT_ACTIVE: "Gradul de ocupare al meselor vine automat din POS. Nu aveți nimic de făcut aici.",
  MGR_POS_HINT_ERROR: "Ultima sincronizare a eșuat; se reia automat. Dacă problema persistă, scrieți-ne.",
  MGR_EVENT_TITLE: "Ce se întâmplă",
  MGR_EVENT_DESC: "Un anunț scurt afișat în banda live (ex. „Jazz live diseară”). Puteți avea unul singur activ.",
  MGR_EVENT_MESSAGE: "Mesaj",
  MGR_EVENT_PH: "Jazz live diseară de la 20:00",
  MGR_EVENT_TYPE: "Tip",
  MGR_EVENT_UPDATE: "Actualizați anunțul",
  MGR_EVENT_PUBLISH: "Publicați anunțul",
  MGR_EVENT_REMOVE: "Ștergeți",
  MGR_EVENT_EMPTY: "Scrieți ceva mai întâi sau ștergeți anunțul.",
  MGR_EVENT_PUBLISHED: "Anunț publicat.",
  MGR_EVENT_SAVE_ERR: "Anunțul nu a putut fi salvat.",
  MGR_EVENT_REMOVED: "Anunț șters.",
  MGR_EVENT_REMOVE_ERR: "Anunțul nu a putut fi șters.",
  MGR_KIND_ANNOUNCEMENT: "Anunț",
  MGR_KIND_PROMO: "Promoție",
  MGR_KIND_LIVE: "Live",
  MGR_KIND_MENU: "Meniu",
  MGR_KIND_INFO: "Info",
  MGR_OFFERS_TITLE: "Oferte și evenimente",
  MGR_OFFERS_DESC: "Reduceri și evenimente speciale afișate pe cardul dvs. Se salvează pe măsură ce editați.",
  MGR_AI_POLISH: "Îmbunătățiți cu AI",
  MGR_AI_POLISH_BUSY: "Se lucrează…",
  MGR_AI_PICK_HINT: "Un singur câmp, o singură limbă: alegeți versiunea pe care o citesc clienții dvs.",
  MGR_AI_DEMO_TITLE: "Text demonstrativ",
  MGR_AI_DEMO_BODY: "nu e configurat încă niciun furnizor AI, așa că acestea sunt exemple, nu oferta dvs.",
  MGR_AI_GENERATED: "Generat cu AI. Verificați textul înainte să salvați.",
  MGR_AI_ERR_AUTH: "Sesiunea a expirat. Conectați-vă din nou.",
  MGR_AI_ERR_RATE: "Prea multe cereri una după alta. Așteptați un minut.",
  MGR_AI_ERR_UNAVAILABLE: "Asistentul AI nu e disponibil momentan.",
  MGR_AI_ERR_NETWORK: "Problemă de conexiune. Încercați din nou.",
  MGR_AI_ERR_UNUSABLE: "Nu a ieșit nimic folositor. Încercați să reformulați.",
  MGR_CONV_TITLE: "Conversii",
  MGR_CONV_DESC: "Clienți aduși de Resto la localul dvs. în ultimele 30 de zile. Luați cifra ca pe un minim, nu ca pe un număr exact: vizitatorii pe care nu îi putem identifica în aplicație nu apar aici, deci numărul real poate fi puțin mai mare. Feedbackul e vizibil doar pentru dvs.",
  MGR_CONV_UNAVAILABLE: "Datele despre conversii nu sunt disponibile încă.",
  MGR_STAT_CONVERSIONS: "Conversii",
  MGR_STAT_FEEDBACK: "Feedback",
  MGR_STAT_RATING: "Notă medie",
  MGR_STAT_SPEND: "Consum mediu",
  // Shared editor chrome on /manage (uploaders, offers editor) — It-3.
  MGR_UPLOAD_UPLOADING: "Se încarcă…",
  MGR_UPLOAD_DROP: "Trageți o imagine aici sau apăsați ca să alegeți una",
  MGR_UPLOAD_REPLACE: "Trageți o imagine nouă sau apăsați ca să o înlocuiți",
  MGR_UPLOAD_REMOVE: "Eliminați",
  MGR_UPLOAD_REMOVE_ARIA: "Elimină imaginea",
  MGR_UPLOAD_ADD: "Adăugați",
  MGR_UPLOAD_MAX: "max. {mb} MB",
  MGR_UPLOAD_MAX_EACH: "max. {mb} MB fiecare",
  MGR_UPLOAD_ERR_TOO_LARGE: "Imaginea depășește limita de {mb} MB.",
  MGR_UPLOAD_ERR_TYPE: "Tip de fișier nesuportat. Folosiți JPG, PNG, WebP sau AVIF.",
  MGR_UPLOAD_ERR: "Încărcarea nu a reușit",
  MGR_OFFER_LOAD_ERR: "Nu am putut încărca ofertele",
  MGR_OFFER_EMPTY: "Încă nu aveți oferte sau evenimente. Adăugați una mai jos.",
  MGR_OFFER_ADD_TITLE: "Adăugați o ofertă sau un eveniment special",
  MGR_OFFER_INFO_PH: "ex. 2 cocktailuri la preț de unul · Jazz live diseară · 20% reducere la felurile principale",
  MGR_OFFER_DESC_PH: "Detalii opționale, afișate când un client apasă pe ofertă…",
  MGR_OFFER_ROW_PH: "Descrieți oferta sau evenimentul…",
  MGR_OFFER_STARTS: "Începe",
  MGR_OFFER_ENDS: "Se termină",
  MGR_OFFER_DAYS: "Zile (niciuna = în fiecare zi)",
  MGR_DAY_SUN: "Dum",
  MGR_DAY_MON: "Lun",
  MGR_DAY_TUE: "Mar",
  MGR_DAY_WED: "Mie",
  MGR_DAY_THU: "Joi",
  MGR_DAY_FRI: "Vin",
  MGR_DAY_SAT: "Sâm",
  MGR_OFFER_REPEATS_WEEKLY: "Se repetă săptămânal",
  MGR_OFFER_ONE_TIME_HINT: "O singură dată: rulează în următoarea zi potrivită, apoi se șterge singură.",
  MGR_OFFER_ADD: "Adăugați",
  MGR_OFFER_REPEATS: "Se repetă",
  MGR_OFFER_ACTIVE: "Activă",
  MGR_OFFER_IMAGE: "Imagine (opțional)",
  MGR_OFFER_ERR_INFO: "Scrieți mai întâi un rând scurt despre ofertă.",
  MGR_OFFER_ERR_TIME: "Orele trebuie să fie HH:MM.",
  MGR_OFFER_ERR_ADD: "Nu am putut adăuga oferta",
  MGR_OFFER_ERR_UPDATE: "Nu am putut salva modificarea",
  MGR_OFFER_ERR_DELETE: "Nu am putut șterge oferta",
  MGR_OFFER_DELETE_TITLE: "Ștergeți intrarea #{n}?",
  MGR_OFFER_DELETE_TITLE_PLAIN: "Ștergeți această intrare?",
  MGR_OFFER_DELETE_DESC: "„{info}” va fi ștearsă. Celelalte intrări se renumerotează automat.",
  MGR_OFFER_NO_DESC: "(fără descriere)",
  MGR_OFFER_DELETE: "Ștergeți",
  MGR_OFFER_DELETE_ARIA: "Ștergeți intrarea #{n}",
  MGR_OFFER_DELETE_ARIA_PLAIN: "Ștergeți intrarea",
  MGR_CANCEL: "Anulați",
};

export const translations = { en, ro } satisfies Record<Lang, Record<keyof typeof en, string>>;

export type TranslationKey = keyof typeof en;
